// Field workspaces UI - Construction and Operations (2026-10-02).
// Design: reports/flow_plan/05_field_workspaces.md. A full-screen,
// mobile-first overlay (#field-app) opened from the "Field" tab, which is
// the landing tab of both workspaces (workspaces.ts).
//
// The phone only CAPTURES and UPLOADS (user decision 2026-10-01): the GPS
// fix, accuracy, altitude and compass heading/pitch are recorded at the
// moment each photo is taken (iOS strips EXIF GPS), the capture goes into
// the IndexedDB outbox (outbox.ts) and uploads when there is signal.
// Matching to assets, road recognition ("you appear to be on X St near Y
// St, facing NE"), thumbnails and EXIF stripping all happen on the server.
// The candidate list is always 5+ suggestions; the crew confirms the asset.
//
// SAFETY: every field screen carries the "not a substitute for beforeUdig or
// a certified locator" banner (config/field_taxonomy.yaml safety_banner).
import maplibregl from "maplibre-gl";
import type { GeoJSONSource, Map as MlMap } from "maplibre-gl";

import "maplibre-gl/dist/maplibre-gl.css"; // was only reached via map.ts, which a field boot no longer loads
import "../styles/field.css";
import { getState, roleAtLeast } from "../state";
import { showToast } from "../toast";
import { fill, h, fmtDate } from "./dom";
import type { AreaAsset, AreaResult, Capture, Candidate, FailureKind, FieldConfig, FieldProject, LiveJob, NearbyResult } from "./fieldApi";
import { FieldCancelledError, failureKind, fieldApi, photoUrl } from "./fieldApi";
import type { AssetPhotos } from "./assetPhotos";
import { fetchAssetPhotos, renderAssetPhotos } from "./assetPhotos";
import { AREA_TILE_Z, Throughput, isStale, loadPlan, mergeTiles, neededTiles, planBatches, tileKey } from "./areaTiles";
import type { Batch, ConnectionInfo, LoadPlan, Tier, TileRef } from "./areaTiles";
import { clearTiles, hydrate, memGet, memPut, persist, pruneTiles } from "./areaTileStore";
import { clampHeight, sheetHeights, snapState, stepState, storedState, tapState } from "./sheet";
import type { SheetHeights, SheetState } from "./sheet";
import { compassPoint, getHeading, onHeading, setDeclinationForLat, startHeading } from "./heading";
import type { HeadingReading } from "./heading";
import { matchOffline, saveArea, listAreas, clearAreas } from "./offline";
import { discard, drain, enqueue, listOutbox, newUuid, onOutboxChange, onUploaded, outboxOwner, retry, setOutboxOwner, startOutbox } from "./outbox";
import { dismissInstallAdvice, installAdvice, promptInstall, resetInstallAdvice } from "./pwa";
import { arrowRotation, directionText, radarDirty, radarHitTest, startRadar, stopRadar } from "./radar";
import type { RadarAsset } from "./radar";
import { guideTarget, remountGuide, startGuide, stopGuide, updateGuide } from "./guide";
import type { GuideItem } from "./guide";
import { preferredDirectionsLink } from "./geoMath";
import type { FieldGuideDetail } from "../directions";
import { FIELD_GUIDE_EVENT } from "../directions";
import { currentWorkspace, switchWorkspace, workspaceChoices } from "../workspaces";
import { CALIBRATION_EVENT, currentCalibration, discardScanQueue, mountAr, resetArCalibration, stopAr } from "./ar";
import { scanSessionsEl } from "./scan";
import { uploadBadge } from "./uploadBadge";
import { addScanSiteLayers, scanSiteClick, updateScanTracks } from "./scanSitesLayer";
import { startTracking, trackGps, trackedFix } from "./track";
import { FACILITY_TOGGLES, addMapIcons, assetColour, assetNetwork, legendEl, mapIconId, symbolIcon } from "./assetStyle";
import { identifyCard } from "./identify";
import { msqaEditing, resumeMsqa, viewMsqa } from "./msqa";
import type { PackArea, PackManifest } from "./fieldApi";
import type { Progress, StoredPack } from "./packs";
import { deletePack, downloadPack, fmtBytes, isExpired, listPacks, packedLayers, packsCovering, storageRoom } from "./packs";
import { openFeedbackDialog } from "../pageFeedback";

type FieldWorkspace = "operations" | "construction";
type View = "near" | "radar" | "ar" | "capture" | "jobs" | "projects" | "mine" | "msqa";
type Basemap = "streets" | "linz";

interface Fix {
  lat: number;
  lon: number;
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy: number | null;
  at: number;
}

interface PendingPhoto {
  blob: Blob;
  url: string;
  heading: number | null;
}

const AREA_RADII = [100, 250, 500];
const AREA_KEY = "wrap.field.areaRadius";

// network facility toggles for the Area layer (2026-10-06): valves (status
// symbols), pump stations, reservoirs / tanks - all on by default
const FACILITY_KEY = "wrap.field.facilities";
type FacilityKey = (typeof FACILITY_TOGGLES)[number]["key"];

function loadFacilities(): Record<FacilityKey, boolean> {
  const out = { valve: true, pump_station: true, reservoir: true } as Record<FacilityKey, boolean>;
  try {
    const v = JSON.parse(localStorage.getItem(FACILITY_KEY) ?? "{}") as Record<string, unknown>;
    for (const k of Object.keys(out) as FacilityKey[]) if (typeof v[k] === "boolean") out[k] = v[k] as boolean;
  } catch {
    /* storage unavailable */
  }
  return out;
}

function saveFacilities(): void {
  try {
    localStorage.setItem(FACILITY_KEY, JSON.stringify(S.facilities));
  } catch {
    /* storage unavailable */
  }
}

/** false when the asset's facility kind is toggled off on the Area layer */
function facilityShown(assetType: string): boolean {
  return !(assetType in S.facilities) || S.facilities[assetType as FacilityKey];
}

function loadAreaRadius(): number {
  try {
    const v = Number(localStorage.getItem(AREA_KEY) ?? "250");
    if (v === 0 || AREA_RADII.includes(v)) return v;
  } catch {
    /* storage unavailable */
  }
  return 250;
}

const S: {
  ws: FieldWorkspace;
  view: View;
  cfg: FieldConfig | null;
  fix: Fix | null;
  heading: HeadingReading;
  nearby: NearbyResult | null;
  offlineCands: Candidate[] | null;
  /** the last /nearby request failed: why (auto-retried with backoff) */
  nearbyError: { kind: FailureKind; message: string } | null;
  lastNearbyOk: number | null;
  /** the "Area" layer: every asset within areaRadius m (0 = off) */
  areaRadius: number;
  area: AreaResult | null;
  areaQuery: { lat: number; lon: number; r: number } | null;
  areaError: FailureKind | null;
  /** Area layer facility toggles (Layers popover) */
  facilities: Record<FacilityKey, boolean>;
  layersOpen: boolean;
  selected: string | null;
  jobs: LiveJob[];
  projects: FieldProject[];
  project: string | null;
  follow: boolean;
  rotate: boolean;
  photos: PendingPhoto[];
  captureFix: Fix | null;
  captureHeading: HeadingReading | null;
  lastQuery: { lat: number; lon: number; heading: number | null; at: number } | null;
  open: boolean;
  basemap: Basemap;
  ldsAvailable: boolean;
  packJob: { name: string; progress: Progress; abort: AbortController } | null;
  packEstimate: { area: PackArea; manifest: PackManifest; free: number | null } | null;
} = {
  ws: "operations",
  view: "near",
  cfg: null,
  fix: null,
  heading: getHeading(),
  nearby: null,
  offlineCands: null,
  nearbyError: null,
  lastNearbyOk: null,
  areaRadius: loadAreaRadius(),
  area: null,
  areaQuery: null,
  areaError: null,
  facilities: loadFacilities(),
  layersOpen: false,
  selected: null,
  jobs: [],
  projects: [],
  project: null,
  follow: true,
  rotate: false,
  photos: [],
  captureFix: null,
  captureHeading: null,
  lastQuery: null,
  open: false,
  basemap: navigator.onLine ? "streets" : "linz",
  ldsAvailable: false,
  packJob: null,
  packEstimate: null,
};

let root: HTMLDivElement | null = null;
let map: MlMap | null = null;
let mapReady = false; // sources/layers added (map 'load' fired)
let watchId: number | null = null;
let sensorsStarted = false;

const VIEWS: Record<FieldWorkspace, { key: View; label: string; icon: string }[]> = {
  operations: [
    { key: "near", label: "Near me", icon: "◎" },
    { key: "radar", label: "Radar", icon: "◉" },
    { key: "ar", label: "AR", icon: "◬" },
    { key: "capture", label: "Capture", icon: "📷" },
    { key: "jobs", label: "Jobs", icon: "⚑" },
    { key: "mine", label: "Mine", icon: "☰" },
  ],
  construction: [
    { key: "projects", label: "Projects", icon: "▦" },
    { key: "msqa", label: "MSQA", icon: "☑" },
    { key: "near", label: "Site map", icon: "◎" },
    { key: "capture", label: "Capture", icon: "📷" },
    { key: "radar", label: "Radar", icon: "◉" },
    { key: "ar", label: "AR", icon: "◬" },
    { key: "mine", label: "Mine", icon: "☰" },
  ],
};

// ---------------------------------------------------------------------------
// Sensors
// ---------------------------------------------------------------------------

function startGps(): void {
  if (watchId !== null || !("geolocation" in navigator)) return;
  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      S.fix = {
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        altitude: pos.coords.altitude,
        altitudeAccuracy: pos.coords.altitudeAccuracy,
        at: pos.timestamp,
      };
      setDeclinationForLat(pos.coords.latitude);
      // the AR overlay draws from the filtered position (track.ts)
      trackGps(S.fix);
      updateStatus();
      updateMe();
      updateGuide();
      radarDirty();
      maybeQueryNearby();
      maybeQueryArea();
      checkAerialCoverage();
    },
    (err) => {
      setStatusText(`Location unavailable: ${err.message}`);
    },
    { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 },
  );
}

async function startSensors(): Promise<void> {
  // iOS: both permission prompts must come from this tap
  const ok = await startHeading();
  startTracking();
  startGps();
  sensorsStarted = true;
  if (!ok) showToast("Compass not available - candidates are ranked by distance only.");
  render();
}

function metresBetween(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = Math.PI / 180;
  const x = (b.lon - a.lon) * r * Math.cos(((a.lat + b.lat) / 2) * r);
  const y = (b.lat - a.lat) * r;
  return Math.sqrt(x * x + y * y) * 6371008.8;
}

let nearbyTimer: number | null = null;
function maybeQueryNearby(force = false): void {
  if (!S.fix || !S.open) return;
  const hd = S.heading.heading;
  const q = S.lastQuery;
  const moved = q ? metresBetween(q, S.fix) : Infinity;
  const turned = q && q.heading !== null && hd !== null ? Math.abs(((hd - q.heading + 540) % 360) - 180) : q && (q.heading === null) !== (hd === null) ? 90 : 0;
  if (!force && q && moved < 5 && turned < 25 && Date.now() - q.at < 30000) return;
  if (nearbyTimer !== null) return;
  // the first list goes out at once, before the Area tiles (nearest first)
  if (!S.nearby && !nearbyInFlight && !S.nearbyError) {
    void queryNearby();
    return;
  }
  nearbyTimer = window.setTimeout(() => {
    nearbyTimer = null;
    void queryNearby();
  }, 600);
}

// After a failed request the movement/turn gate above waits for the user to
// move 5 m, turn 25 deg or for 30 s (the Washbournes Rd walk-out,
// 2026-10-05: a Wi-Fi -> mobile hand-over while the server was busy left
// the app on "no signal" for good). So a failure retries on its own with
// backoff (3, 6, 12, then every 15 s), and on the browser's online event and
// when the app becomes visible again (retryNow).
const RETRY_FIRST_MS = 3000;
const RETRY_MAX_MS = 15000;

class Retry {
  private timer: number | null = null;
  private delay = RETRY_FIRST_MS;
  constructor(private readonly run: () => void) {}
  schedule(): void {
    if (this.timer !== null) return;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      if (S.open) this.run();
    }, this.delay);
    this.delay = Math.min(RETRY_MAX_MS, this.delay * 2);
  }
  reset(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.delay = RETRY_FIRST_MS;
  }
}

const nearbyRetry = new Retry(() => void queryNearby());
const areaRetry = new Retry(() => queryArea());
let nearbyInFlight = false;

async function useOfflineFallback(fix: Fix, heading: number | null): Promise<void> {
  // the phone's cached area if it has one there; otherwise keep showing the
  // last server list (the retry line says it's from an earlier fix)
  const cached = (await matchOffline(fix.lat, fix.lon, heading, fix.accuracy)) as unknown as Candidate[];
  S.offlineCands = cached.length || !S.nearby ? cached : null;
}

async function queryNearby(): Promise<void> {
  if (!S.fix || nearbyInFlight) return;
  const fix = S.fix;
  const hd = S.heading;
  S.lastQuery = { lat: fix.lat, lon: fix.lon, heading: hd.heading, at: Date.now() };
  if (!navigator.onLine) {
    S.nearbyError = { kind: "offline", message: "" };
    await useOfflineFallback(fix, hd.heading);
    nearbyRetry.schedule();
    renderView();
    return;
  }
  let result: NearbyResult;
  nearbyInFlight = true;
  try {
    // working on a project: its own assets and network rank first
    result = await fieldApi.nearby({ lat: fix.lat, lon: fix.lon, heading: hd.heading, heading_accuracy: hd.accuracy, accuracy: fix.accuracy,
      project_id: S.cfg?.profile !== "public" ? S.project : null });
  } catch (e) {
    // "offline" only for a failed request (no network); a timeout reads as a
    // slow server and a 4xx/5xx as a server error. Each one retries.
    S.nearbyError = { kind: failureKind(e), message: e instanceof Error ? e.message : String(e) };
    nearbyInFlight = false;
    await useOfflineFallback(fix, hd.heading);
    nearbyRetry.schedule();
    renderView();
    console.warn("nearby failed", e);
    return;
  } finally {
    nearbyInFlight = false;
    if (areaHeldSince !== null) queueMicrotask(() => queryArea()); // the Area was waiting for this list
  }
  S.nearby = result;
  S.nearbyError = null;
  S.lastNearbyOk = Date.now();
  nearbyRetry.reset();
  S.offlineCands = null; // the server result replaces the offline one
  try {
    updateMapAssets();
    renderView();
  } catch (e) {
    console.error("rendering nearby assets failed", e); // a display bug, not lost signal
  }
}

// --- the "Area" layer: every asset within S.areaRadius, drawn faintly -------
// Loaded in z19 tiles, nearest first (50 m, 150 m, then the radius), in
// batches sized to the connection; each batch is drawn as it arrives and
// kept (areaTileStore.ts), so walking on only fetches new tiles and a stale
// batch is cancelled (2026-10-08, areaTiles.ts).

const areaLoad: {
  /** tile keys needed for the current fix + radius (incl. prefetch), nearest first */
  needed: TileRef[];
  neededKeys: Set<string>;
  /** the tiles inside the radius itself (what the map draws) */
  shownKeys: string[];
  plan: LoadPlan | null;
  /** batches on the wire (two at once on a slow link: it hides the latency) */
  inFlight: { keys: string[]; ac: AbortController }[];
  meta: Pick<AreaResult, "profile" | "road_source" | "area_note" | "test_regions"> | null;
  /** perf log for the field app's own measurements (window.__fieldArea) */
  log: { t: number; tiles: number; n: number; bytes: number | null; ms: number; stage: number; tier: Tier }[];
  firstAt: number | null;
  doneAt: number | null;
  startedAt: number | null;
} = { needed: [], neededKeys: new Set(), shownKeys: [], plan: null, inFlight: [], meta: null, log: [], firstAt: null, doneAt: null, startedAt: null };
const throughput = new Throughput();

function tileOwner(): string {
  return `${getState().currentUser?.username ?? "?"}:${S.cfg?.profile ?? "full"}`;
}

function connectionInfo(): ConnectionInfo | null {
  const c = (navigator as Navigator & { connection?: ConnectionInfo }).connection;
  return c ? { effectiveType: c.effectiveType, downlink: c.downlink, rtt: c.rtt, saveData: c.saveData } : null;
}

/** Recompute the tiles this fix + radius needs; cancel a batch that is no
 * longer needed; draw what is cached; fetch the rest. */
function maybeQueryArea(force = false): void {
  if (!S.fix || !S.open || !S.areaRadius || S.view === "ar") return;
  const q = S.areaQuery;
  if (!force && q && q.r === S.areaRadius && metresBetween(q, S.fix) < 15) return;
  const fix = S.fix;
  const plan = loadPlan(S.areaRadius, connectionInfo(), throughput.measured);
  S.areaQuery = { lat: fix.lat, lon: fix.lon, r: S.areaRadius };
  areaLoad.plan = plan;
  areaLoad.needed = neededTiles(fix.lat, fix.lon, plan.radiusM + plan.prefetchM);
  areaLoad.neededKeys = new Set(areaLoad.needed.map((t) => tileKey(AREA_TILE_Z, t.x, t.y)));
  areaLoad.shownKeys = areaLoad.needed.filter((t) => t.d <= plan.radiusM).map((t) => tileKey(AREA_TILE_Z, t.x, t.y));
  for (const fl of areaLoad.inFlight) {
    if (isStale(fl.keys, areaLoad.neededKeys)) fl.ac.abort(); // the crew walked on
  }
  areaLoad.inFlight = areaLoad.inFlight.filter((fl) => !fl.ac.signal.aborted);
  const owner = tileOwner();
  rebuildArea(); // what this session already holds, at once
  void hydrate(owner, [...areaLoad.neededKeys]).then((n) => {
    if (n) rebuildArea(); // + what earlier sessions left in IndexedDB
    queryArea();
  });
}

/** S.area from the loaded tiles inside the radius (drawn at once). */
function rebuildArea(): void {
  const owner = tileOwner();
  const m = mergeTiles(areaLoad.shownKeys.map((k) => memGet(owner, k)));
  const q = S.areaQuery;
  if (!q) return;
  const have = areaLoad.shownKeys.filter((k) => memGet(owner, k)).length;
  S.area = {
    center: [q.lon, q.lat],
    radius_m: areaLoad.plan?.radiusM ?? q.r,
    max_radius_m: 500,
    n: m.assets.length,
    assets: m.assets,
    roads: m.roads,
    profile: areaLoad.meta?.profile ?? S.cfg?.profile ?? "full",
    road_source: areaLoad.meta?.road_source ?? "LINZ NZ Road Centrelines (Topo50), CC BY 4.0",
    area_note: areaLoad.meta?.area_note,
    test_regions: areaLoad.meta?.test_regions,
    loaded_tiles: have,
    total_tiles: areaLoad.shownKeys.length,
  };
  updateAreaLayer();
  if (S.layersOpen) renderMapControls(); // the popover's per-kind counts
  renderMapHint();
}

function exposeAreaPerf(): void {
  (window as unknown as { __fieldArea?: unknown }).__fieldArea = {
    firstAt: areaLoad.firstAt, doneAt: areaLoad.doneAt, startedAt: areaLoad.startedAt, tier: areaLoad.plan?.tier,
    basis: areaLoad.plan?.basis, chunks: areaLoad.log,
  };
}

/** The first nearby list is on its way: the Area waits for it (one server
 * worker - the list is what the crew looks at first), at most 1.5 s. */
let areaHeldSince: number | null = null;

/** Fetch the missing tiles batch by batch, nearest ring first (one request
 * at a time on a fast link, two on a slow one), until the radius (+ prefetch)
 * is loaded. Each batch is drawn when it lands. */
function queryArea(): void {
  if (!S.fix || !S.areaRadius || !areaLoad.plan) return;
  if (nearbyInFlight && !S.nearby) {
    if (areaHeldSince === null) areaHeldSince = Date.now();
    if (Date.now() - areaHeldSince < 1500) {
      window.setTimeout(() => queryArea(), 300);
      return;
    }
  }
  areaHeldSince = null;
  const owner = tileOwner();
  const plan = areaLoad.plan;
  const busy = new Set(areaLoad.inFlight.flatMap((f) => f.keys));
  const batches = planBatches(areaLoad.needed, plan,
    (t) => { const k = tileKey(AREA_TILE_Z, t.x, t.y); return busy.has(k) || !!memGet(owner, k); });
  if (!batches.length) {
    if (areaLoad.inFlight.length) return; // the last ones are still landing
    S.areaError = null;
    areaRetry.reset();
    if (areaLoad.startedAt !== null && areaLoad.doneAt === null) {
      areaLoad.doneAt = performance.now();
      try {
        performance.mark("field-area-complete");
      } catch {
        /* no User Timing */
      }
    }
    exposeAreaPerf();
    renderMapHint();
    return;
  }
  if (!navigator.onLine) {
    S.areaError = "offline";
    areaRetry.schedule();
    renderMapHint();
    return;
  }
  const lanes = plan.tier === "fast" ? 1 : 2;
  for (const b of batches) {
    if (areaLoad.inFlight.length >= lanes) break;
    void fetchBatch(b, plan, owner);
  }
}

async function fetchBatch(b: Batch, plan: LoadPlan, owner: string): Promise<void> {
  const keys = b.tiles.map((t) => tileKey(AREA_TILE_Z, t.x, t.y));
  const ac = new AbortController();
  const entry = { keys, ac };
  areaLoad.inFlight.push(entry);
  if (areaLoad.startedAt === null) areaLoad.startedAt = performance.now();
  try {
    const q = S.areaQuery;
    // the whole stage + a tile diagonal: its other batches then come from the server's cache
    const around = q ? { lat: q.lat, lon: q.lon, r: Math.min(620, b.stage + 90) } : null;
    const res = await fieldApi.areaTiles(AREA_TILE_Z, b.tiles.map((t) => `${t.x}:${t.y}`), b.coarse.map((t) => `${t.x}:${t.y}`), around, ac.signal);
    throughput.add(res.bytes ?? 0, res.ms, res.ttfbMs);
    const d = res.data;
    areaLoad.meta = { profile: d.profile, road_source: d.road_source, area_note: d.area_note, test_regions: d.test_regions };
    const now = Date.now();
    const stored = Object.entries(d.tiles).map(([xy, v]) => {
      const [x, y] = xy.split(":").map(Number);
      return memPut(owner, { key: tileKey(d.z, x, y), at: now, assets: v.assets, roads: v.roads, coarse: b.coarse.some((t) => t.x === x && t.y === y) });
    });
    void persist(owner, stored);
    areaLoad.log.push({ t: Math.round(performance.now()), tiles: keys.length, n: d.n, bytes: res.bytes, ms: Math.round(res.ms), stage: b.stage, tier: plan.tier });
    if (areaLoad.firstAt === null) {
      areaLoad.firstAt = performance.now();
      try {
        performance.mark("field-area-first");
      } catch {
        /* no User Timing */
      }
    }
    S.areaError = null;
    areaRetry.reset();
    exposeAreaPerf();
    rebuildArea();
  } catch (e) {
    if (e instanceof FieldCancelledError) return; // the crew walked on: maybeQueryArea started over
    S.areaError = failureKind(e); // what's loaded stays on the map meanwhile
    areaRetry.schedule();
    console.warn("area tiles failed", e);
    renderMapHint();
    return;
  } finally {
    areaLoad.inFlight = areaLoad.inFlight.filter((f) => f !== entry);
  }
  // the connection estimate may have changed the plan (chunk size, prefetch)
  if (S.fix && S.areaRadius) {
    const next = loadPlan(S.areaRadius, connectionInfo(), throughput.measured);
    if (next.tier !== plan.tier && !areaLoad.inFlight.length) maybeQueryArea(true);
    else queryArea();
  }
}

/** Retry now (back online, app visible again): skip any backoff wait. */
function retryNow(): void {
  if (!S.open) return;
  nearbyRetry.reset();
  areaRetry.reset();
  if (S.nearbyError) void queryNearby();
  else maybeQueryNearby(true);
  S.areaError = null;
  maybeQueryArea(true);
}

function setAreaRadius(r: number): void {
  S.areaRadius = r;
  try {
    localStorage.setItem(AREA_KEY, String(r));
  } catch {
    /* storage unavailable */
  }
  areaRetry.reset();
  S.areaError = null;
  if (!r) {
    for (const f of areaLoad.inFlight) f.ac.abort();
    areaLoad.inFlight = [];
    S.area = null;
    S.areaQuery = null;
    updateAreaLayer();
  } else {
    maybeQueryArea(true);
  }
  renderMapControls();
  renderMapHint();
}

/** An Area asset as a Candidate (distance and bearing from the user worked
 * out here), so the identify card, the map highlight and the capture form
 * treat a tapped Area asset like a ranked suggestion. */
function areaCandidate(a: AreaAsset): Candidate {
  const o = S.fix ?? { lat: S.area?.center[1] ?? 0, lon: S.area?.center[0] ?? 0 };
  const k = (Math.PI / 180) * 6371008.8;
  const cosLat = Math.cos((o.lat * Math.PI) / 180);
  const xy = (p: [number, number]): [number, number] => [(p[0] - o.lon) * k * cosLat, (p[1] - o.lat) * k];
  const pts = a.path?.length ? a.path.map(xy) : a.lon !== undefined && a.lat !== undefined ? [xy([a.lon, a.lat])] : [];
  let best: [number, number] = [0, 0];
  let bestD = Infinity;
  for (let i = 0; i < pts.length; i++) {
    // nearest point of each segment to the user (at the origin)
    const p = pts[i];
    const q = pts[i + 1] ?? p;
    const dx = q[0] - p[0];
    const dy = q[1] - p[1];
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(p[0] * dx + p[1] * dy) / len2)) : 0;
    const c: [number, number] = [p[0] + t * dx, p[1] + t * dy];
    const d = Math.hypot(c[0], c[1]);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  const dist = Number.isFinite(bestD) ? bestD : 0;
  const bearing = dist > 0.5 ? ((Math.atan2(best[0], best[1]) * 180) / Math.PI + 360) % 360 : null;
  const attribution = a.test_region ? S.area?.test_regions?.find((t) => t.key === a.test_region)?.attribution : undefined;
  return {
    ...a,
    kind: a.path?.length ? "line" : "point",
    attribution,
    distance_m: dist,
    bearing_deg: bearing,
    compass: bearing === null ? null : compassPoint(bearing),
    relative_bearing_deg: null,
    along_road: false,
    score: 0,
    rank: 0,
    reasons: ["in the Area layer (not ranked)"],
  };
}

function candidates(): Candidate[] {
  if (S.offlineCands) return projectFirst(S.offlineCands);
  return S.nearby?.candidates ?? [];
}

/** Offline ranking (computed on the phone): the selected project's own
 * pipes first, as the server does for /nearby?project_id=. */
function projectFirst(cands: Candidate[]): Candidate[] {
  if (!S.project || !projectData.features.length) return cands;
  const ids = new Set(projectData.features.map((f) => String(f.properties?.asset_id ?? "")));
  const mine = cands.filter((c) => ids.has(c.asset_id)).map((c) => ({ ...c, in_project: true }));
  return mine.length ? [...mine, ...cands.filter((c) => !ids.has(c.asset_id))] : cands;
}

// ---------------------------------------------------------------------------
// Map (follow-me)
// ---------------------------------------------------------------------------

function circle(lat: number, lon: number, radiusM: number, steps = 48): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    const dy = (radiusM * Math.cos(a)) / 111320;
    const dx = (radiusM * Math.sin(a)) / (111320 * Math.cos((lat * Math.PI) / 180));
    pts.push([lon + dx, lat + dy]);
  }
  return pts;
}

function wedge(lat: number, lon: number, heading: number, lenM = 25, half = 20): [number, number][] {
  const pts: [number, number][] = [[lon, lat]];
  for (let a = heading - half; a <= heading + half; a += 5) {
    const r = (a * Math.PI) / 180;
    pts.push([lon + (lenM * Math.sin(r)) / (111320 * Math.cos((lat * Math.PI) / 180)), lat + (lenM * Math.cos(r)) / 111320]);
  }
  pts.push([lon, lat]);
  return pts;
}

const EMPTY = { type: "FeatureCollection", features: [] } as GeoJSON.FeatureCollection;

const STREETS_STYLE = "https://tiles.openfreemap.org/styles/positron";
const LINZ_ATTRIBUTION = "© Toitū Te Whenua LINZ (CC BY 4.0)";
let projectData: GeoJSON.FeatureCollection = EMPTY;

/** Inline raster style on LINZ Data Service tiles through our proxy. Used
 * for the aerial basemap and OFFLINE (it has no remote style/glyph
 * dependency, and its tile URLs are exactly what offline packs store).
 * Online: rural under urban aerial; offline: the layers packs hold. */
function linzStyle(layers: string[]): maplibregl.StyleSpecification {
  const order = ["hillshade", "topo50", "aerial_rural", "aerial_urban", "aerial"].filter((l) => layers.includes(l));
  const sources: maplibregl.StyleSpecification["sources"] = {};
  for (const l of order) {
    sources[`lds-${l}`] = {
      type: "raster",
      tiles: [`${window.location.origin}/api/tiles/lds/${l}/{z}/{x}/{y}.png`],
      tileSize: 256,
      maxzoom: l === "topo50" ? 15 : 19,
      attribution: LINZ_ATTRIBUTION,
    };
  }
  return {
    version: 8,
    // online only: OpenFreeMap's fonts, for the LINZ road-name labels drawn
    // over the aerial (offline the labels just don't render)
    ...(navigator.onLine ? { glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf" } : {}),
    sources,
    layers: [
      { id: "bg", type: "background", paint: { "background-color": "#e2e8f0" } },
      ...order.map((l) => ({ id: `lds-${l}`, type: "raster" as const, source: `lds-${l}` })),
    ],
  };
}

/** Online: the proxy's nationwide "aerial" (the best LINZ photos for each
 * tile, 2026-10-08 - it was the Wellington layers only, so the aerial never
 * loaded in Christchurch); offline: the layers the packs hold. */
async function linzLayersNow(): Promise<string[]> {
  if (navigator.onLine) return ["aerial"];
  const packed = [...(await packedLayers())];
  return packed.length ? packed : ["aerial"];
}

// --- why the aerial isn't showing (2026-10-08): never fail silently ---------

const aerialState: { reason: string | null; errors: number; okTiles: number; coverage: { lat: number; lon: number; covered: boolean } | null } = {
  reason: null, errors: 0, okTiles: 0, coverage: null,
};

/** A reason the LINZ aerial can't be shown, from a tile's HTTP status. */
function aerialReason(status: number | undefined): string {
  if (!navigator.onLine) return "no signal, and no offline map pack covers this spot";
  if (status === 503) return "the server has no LINZ key configured";
  if (status === 401 || status === 403) return "you are signed out - sign in again";
  if (status === 429) return "too many tile requests - try again shortly";
  if (status === 502 || status === 504) return "LINZ's tile service isn't answering";
  if (status === 404) return "LINZ has no imagery for this tile";
  return status ? `tile error (HTTP ${status})` : "tiles failed to load";
}

function aerialHint(): string | null {
  if (S.basemap !== "linz") return null;
  if (aerialState.reason) return `Aerial unavailable: ${aerialState.reason}.`;
  if (aerialState.coverage && !aerialState.coverage.covered) {
    return "No LINZ aerial photos cover this spot: showing 10 m satellite imagery instead.";
  }
  return null;
}

function setAerialReason(reason: string | null): void {
  if (aerialState.reason === reason) return;
  aerialState.reason = reason;
  renderMapHint();
  renderMapControls();
}

/** Ask the server which LINZ aerial layers cover the fix (by extent; no
 * tile fetch) when the aerial is on and the crew moved 2 km. */
function checkAerialCoverage(): void {
  if (S.basemap !== "linz" || !S.fix || !navigator.onLine) return;
  const c = aerialState.coverage;
  if (c && metresBetween(c, S.fix) < 2000) return;
  const { lat, lon } = S.fix;
  aerialState.coverage = { lat, lon, covered: true };
  fetch(`/api/tiles/lds/aerial/coverage?lat=${lat.toFixed(5)}&lon=${lon.toFixed(5)}`, { credentials: "same-origin" })
    .then((r) => (r.ok ? r.json() : null))
    .then((j: { covered?: boolean; available?: boolean } | null) => {
      if (!j) return;
      aerialState.coverage = { lat, lon, covered: !!j.covered };
      if (j.available === false) setAerialReason("the server has no LINZ key configured");
      renderMapHint();
    })
    .catch(() => undefined);
}

async function setBasemap(b: Basemap): Promise<void> {
  if (b === "linz" && !S.ldsAvailable && navigator.onLine && aerialState.reason) {
    showToast(`Aerial unavailable: ${aerialState.reason}.`);
    return;
  }
  S.basemap = b;
  aerialState.errors = 0;
  aerialState.okTiles = 0;
  if (b === "linz") checkAerialCoverage();
  if (!map) return;
  mapReady = false;
  // diff: false - a diffed setStyle removes our overlay sources/layers (they
  // aren't in either style) and fires no 'style.load', so the aerial showed
  // with no pipes and no blue dot. A full reload always re-runs style.load,
  // which re-adds the overlays and pushes their data back.
  map.setStyle(b === "streets" ? STREETS_STYLE : linzStyle(await linzLayersNow()), { diff: false });
  renderMapControls();
}

function initMap(container: HTMLElement): void {
  if (map) return;
  map = new maplibregl.Map({
    container,
    style: S.basemap === "streets" ? STREETS_STYLE : linzStyle(["aerial", "aerial_urban", "aerial_rural", "topo50"]),
    center: [174.7762, -41.2865],
    zoom: 16,
    attributionControl: { compact: true },
  });
  // tap-to-identify: the ranked suggestions first, then the faint Area
  // layer; a 10 px box so a thin line is easy to hit with a finger
  map.on("click", (e) => {
    if (!map) return;
    if (scanSiteClick(map, e)) return; // a scanned site / capture track: open the viewer
    const order = ["points-l", "pipes-l", "area-points-l", "area-pipes-l"].filter((l) => map?.getLayer(l));
    const box: [[number, number], [number, number]] = [[e.point.x - 10, e.point.y - 10], [e.point.x + 10, e.point.y + 10]];
    const feats = map.queryRenderedFeatures(box, { layers: order });
    for (const l of order) {
      const id = feats.find((f) => f.layer.id === l)?.properties?.asset_id as string | undefined;
      if (id) {
        selectAsset(id);
        return;
      }
    }
  });
  // a failing LINZ tile says why the aerial isn't showing (map hint), rather
  // than leaving a grey map; one good tile clears it
  map.on("error", (e: { sourceId?: string; error?: { status?: number } }) => {
    if (!e.sourceId?.startsWith("lds-") || S.basemap !== "linz") return;
    aerialState.errors++;
    if (aerialState.errors >= 3 && aerialState.okTiles === 0) setAerialReason(aerialReason(e.error?.status));
  });
  map.on("data", (e: { sourceId?: string; dataType?: string; tile?: unknown }) => {
    if (e.dataType !== "source" || !e.tile || !e.sourceId?.startsWith("lds-")) return;
    aerialState.okTiles++;
    if (aerialState.reason) setAerialReason(null);
  });
  // overlays are re-added on every style load (streets <-> LINZ aerial)
  map.on("style.load", () => {
    if (!map) return;
    mapReady = true;
    for (const id of ["acc", "wedge", "me", "cal-me", "cal-link", "pipes", "points", "jobs", "project", "selected", "roads", "area-pipes", "area-points"]) {
      if (!map.getSource(id)) map.addSource(id, { type: "geojson", data: id === "project" ? projectData : EMPTY });
    }
    if (map.getLayer("project-l")) return;
    map.addLayer({ id: "project-l", type: "line", source: "project", paint: { "line-color": "#805ad5", "line-width": 6, "line-opacity": 0.55 } });
    // LINZ road centrelines + names: only on the LINZ aerial, which has no
    // streets of its own (the OpenFreeMap streets style already has both)
    if (S.basemap === "linz") {
      map.addLayer({ id: "roads-l", type: "line", source: "roads", paint: { "line-color": "#fff", "line-width": 2, "line-opacity": 0.55 } });
      if (map.getStyle().glyphs) {
        map.addLayer({ id: "roads-label", type: "symbol", source: "roads",
          layout: { "symbol-placement": "line", "text-field": ["get", "name"], "text-font": ["Noto Sans Regular"], "text-size": 12 },
          paint: { "text-color": "#1a202c", "text-halo-color": "#fff", "text-halo-width": 1.6 } });
      }
    }
    // points: network colour + a symbol per type (assetStyle.ts), drawn as
    // images so the offline LINZ style (no glyphs) shows them too
    addMapIcons(map);
    // the Area layer: every asset within the chosen radius, faint
    map.addLayer({ id: "area-pipes-l", type: "line", source: "area-pipes",
      paint: { "line-color": ["get", "colour"], "line-width": ["interpolate", ["linear"], ["zoom"], 14, 1, 18, 2.5], "line-opacity": 0.45 } });
    map.addLayer({ id: "area-points-l", type: "symbol", source: "area-points",
      layout: { "icon-image": ["get", "icon"], "icon-size": ["interpolate", ["linear"], ["zoom"], 14, 0.35, 18, 0.7],
        "icon-allow-overlap": true, "icon-ignore-placement": true },
      paint: { "icon-opacity": 0.55 } });
    // the ranked suggestions on top, emphasised (white casing, thicker, bigger)
    map.addLayer({ id: "pipes-casing", type: "line", source: "pipes", paint: { "line-color": "#fff", "line-width": 8, "line-opacity": 0.9 } });
    map.addLayer({ id: "pipes-l", type: "line", source: "pipes", paint: { "line-color": ["get", "colour"], "line-width": 4.5 } });
    map.addLayer({ id: "selected-l", type: "line", source: "selected", filter: ["==", ["geometry-type"], "LineString"],
      paint: { "line-color": "#f6e05e", "line-width": 10, "line-opacity": 0.8 } });
    map.addLayer({ id: "selected-c", type: "circle", source: "selected", filter: ["==", ["geometry-type"], "Point"],
      paint: { "circle-radius": 15, "circle-color": "#f6e05e", "circle-opacity": 0.8 } });
    map.addLayer({ id: "points-l", type: "symbol", source: "points",
      layout: { "icon-image": ["get", "icon"], "icon-size": 1.15, "icon-allow-overlap": true, "icon-ignore-placement": true } });
    // the organisation's scanned sites + capture tracks (2026-10-15, scanSitesLayer.ts)
    addScanSiteLayers(map);
    map.addLayer({ id: "jobs-l", type: "circle", source: "jobs", paint: { "circle-radius": 9, "circle-color": "#d69e2e", "circle-stroke-color": "#1a202c", "circle-stroke-width": 2 } });
    map.addLayer({ id: "acc-l", type: "fill", source: "acc", paint: { "fill-color": "#4299e1", "fill-opacity": 0.15 } });
    map.addLayer({ id: "wedge-l", type: "fill", source: "wedge", paint: { "fill-color": "#2b6cb0", "fill-opacity": 0.35 } });
    // An AR calibration is NOT applied to this map (2026-10-05): the blue
    // dot stays raw GPS; where the AR calibration thinks you are is a
    // hollow amber ring joined to it, so a bad calibration shows as a ring
    // metres away from where you stand rather than moving the whole map.
    map.addLayer({ id: "cal-link-l", type: "line", source: "cal-link", paint: { "line-color": "#b7791f", "line-width": 2, "line-dasharray": [2, 2] } });
    map.addLayer({ id: "cal-me-l", type: "circle", source: "cal-me",
      paint: { "circle-radius": 9, "circle-color": "rgba(0,0,0,0)", "circle-stroke-color": "#b7791f", "circle-stroke-width": 3 } });
    map.addLayer({ id: "me-l", type: "circle", source: "me", paint: { "circle-radius": 8, "circle-color": "#3182ce", "circle-stroke-color": "#fff", "circle-stroke-width": 3 } });
    updateMe();
    updateMapAssets();
    updateAreaLayer();
  });
  // the container moves between re-renders and the layout changes between
  // views: keep the canvas sized to it
  new ResizeObserver(() => map?.resize()).observe(container);
  // pan freely: dragging pauses follow-me ("Re-centre" brings it back);
  // pinch-zoom / rotate keep following, but the follow easeTo doesn't
  // fight the fingers while they are down
  map.on("dragstart", () => {
    if (S.follow) {
      S.follow = false;
      renderMapControls();
    }
  });
  const gestureOn = (e: { originalEvent?: unknown }) => {
    if (e.originalEvent) userGesture = true;
  };
  const gestureOff = () => {
    userGesture = false;
  };
  map.on("zoomstart", gestureOn);
  map.on("rotatestart", gestureOn);
  map.on("pitchstart", gestureOn);
  map.on("zoomend", gestureOff);
  map.on("moveend", () => {
    if (map) void updateScanTracks(map);
  });
  map.on("rotateend", gestureOff);
  map.on("pitchend", gestureOff);
}

let userGesture = false;

function src(id: string): GeoJSONSource | null {
  return (map?.getSource(id) as GeoJSONSource | undefined) ?? null;
}

function updateMe(): void {
  if (!map || !S.fix || !mapReady) return;
  const { lat, lon, accuracy } = S.fix;
  src("me")?.setData({ type: "Feature", geometry: { type: "Point", coordinates: [lon, lat] }, properties: {} });
  src("acc")?.setData({ type: "Feature", geometry: { type: "Polygon", coordinates: [circle(lat, lon, accuracy)] }, properties: {} });
  const hd = S.heading.heading;
  src("wedge")?.setData(hd === null ? EMPTY : { type: "Feature", geometry: { type: "Polygon", coordinates: [wedge(lat, lon, hd)] }, properties: {} });
  const cal = currentCalibration(S.fix);
  const shift = cal ? Math.hypot(cal.east_m, cal.north_m) : 0;
  // an anchored calibration (2026-10-05) lives in the tracker: the amber
  // ring is the tracked position; an older one is raw GPS + its offset
  const tf = cal?.pos_anchor ? trackedFix() : null;
  if (cal && (tf || shift >= 0.3)) {
    const clat = tf ? tf.lat : lat + cal.north_m / 111320;
    const clon = tf ? tf.lon : lon + cal.east_m / (111320 * Math.cos((lat * Math.PI) / 180));
    src("cal-me")?.setData({ type: "Feature", geometry: { type: "Point", coordinates: [clon, clat] }, properties: {} });
    src("cal-link")?.setData({ type: "Feature", geometry: { type: "LineString", coordinates: [[lon, lat], [clon, clat]] }, properties: {} });
  } else {
    src("cal-me")?.setData(EMPTY);
    src("cal-link")?.setData(EMPTY);
  }
  if (S.follow && !userGesture) {
    map.easeTo({ center: [lon, lat], bearing: S.rotate && hd !== null ? hd : map.getBearing(), duration: 400 });
  }
}

function updateMapAssets(): void {
  if (!map || !mapReady) return;
  const cands = candidates();
  const lines: GeoJSON.Feature[] = [];
  const pts: GeoJSON.Feature[] = [];
  for (const c of cands) {
    const props = { asset_id: c.asset_id, colour: assetColour(c), icon: mapIconId(c.asset_type, assetNetwork(c), c.normal_status) };
    if (c.kind === "line" && c.path) lines.push({ type: "Feature", geometry: { type: "LineString", coordinates: c.path }, properties: props });
    else if (c.lon !== undefined && c.lat !== undefined) pts.push({ type: "Feature", geometry: { type: "Point", coordinates: [c.lon, c.lat] }, properties: props });
  }
  src("pipes")?.setData({ type: "FeatureCollection", features: lines });
  src("points")?.setData({ type: "FeatureCollection", features: pts });
  const sel = selectedCandidate();
  src("selected")?.setData(
    sel?.kind === "line" && sel.path ? { type: "Feature", geometry: { type: "LineString", coordinates: sel.path }, properties: {} }
      : sel && sel.lon !== undefined && sel.lat !== undefined ? { type: "Feature", geometry: { type: "Point", coordinates: [sel.lon, sel.lat] }, properties: {} }
        : EMPTY,
  );
  src("jobs")?.setData({
    type: "FeatureCollection",
    features: S.view === "jobs" ? S.jobs.map((j) => ({ type: "Feature", geometry: { type: "Point", coordinates: [j.lon, j.lat] }, properties: {} }) as GeoJSON.Feature) : [],
  });
}

function updateAreaLayer(): void {
  if (!map || !mapReady) return;
  const lines: GeoJSON.Feature[] = [];
  const pts: GeoJSON.Feature[] = [];
  for (const a of S.area?.assets ?? []) {
    if (!facilityShown(a.asset_type)) continue;
    const props = { asset_id: a.asset_id, colour: assetColour(a), icon: mapIconId(a.asset_type, assetNetwork(a), a.normal_status) };
    if (a.path && a.path.length >= 2) lines.push({ type: "Feature", geometry: { type: "LineString", coordinates: a.path }, properties: props });
    else if (a.lon !== undefined && a.lat !== undefined) pts.push({ type: "Feature", geometry: { type: "Point", coordinates: [a.lon, a.lat] }, properties: props });
  }
  src("area-pipes")?.setData({ type: "FeatureCollection", features: lines });
  src("area-points")?.setData({ type: "FeatureCollection", features: pts });
  src("roads")?.setData({
    type: "FeatureCollection",
    features: (S.area?.roads ?? []).filter((r) => r.path.length >= 2)
      .map((r) => ({ type: "Feature", geometry: { type: "LineString", coordinates: r.path }, properties: { name: r.name } }) as GeoJSON.Feature),
  });
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

let statusEl: HTMLElement | null = null;
let viewEl: HTMLElement | null = null;
let navEl: HTMLElement | null = null;
let controlsEl: HTMLElement | null = null;
let hintEl: HTMLElement | null = null;
let badgeEl: HTMLElement | null = null;

const ROAD_HINT_M = 60;

/** "You're X m from <road> ..." when the nearest road is far: at a rear
 * site the assets along the street can be outside the map view. */
function roadHint(): string | null {
  const rc = S.nearby?.road;
  if (!rc || !S.fix) return null;
  const tip = S.areaRadius >= 500 ? "zoom out to look along it." : "zoom out or use Area 500 m.";
  const r = rc.road;
  if (!r) {
    return rc.sentence?.startsWith("No mapped road")
      ? `${rc.sentence.replace(/\.$/, "")} — assets along the street may be beyond this view; ${tip}`
      : null;
  }
  if (r.distance_m <= ROAD_HINT_M) return null;
  return `You're ${Math.round(r.distance_m)} m from ${r.name} — assets along the street may be beyond this view; ${tip}`;
}

function renderMapHint(): void {
  if (!hintEl) return;
  const road = roadHint();
  const a = S.area;
  const loading = S.areaRadius && a && a.total_tiles && (a.loaded_tiles ?? 0) < a.total_tiles;
  const area = S.areaRadius && S.areaError
    ? `Area layer: ${S.areaError === "offline" ? "no signal" : S.areaError === "slow" ? "server slow" : "server error"} - retrying…${loading ? ` (${a?.loaded_tiles}/${a?.total_tiles} tiles loaded)` : ""}`
    : loading && areaLoad.plan?.tier === "slow"
      ? `Area layer: loading nearest first on a slow connection (${a?.loaded_tiles}/${a?.total_tiles} tiles)`
      : null;
  const capped = S.areaRadius && areaLoad.plan?.capped && areaLoad.plan.radiusM < S.areaRadius
    ? `Area shows ${areaLoad.plan.radiusM} m, not ${S.areaRadius} m: ${areaLoad.plan.capped}.`
    : null;
  fill(hintEl, road ? h("div", {}, road) : null, area ? h("div", { class: "fa-small" }, area) : null,
    capped ? h("div", { class: "fa-small" }, capped) : null, aerialHint() ? h("div", { class: "fa-small" }, aerialHint()) : null);
  hintEl.hidden = !road && !area && !capped && !aerialHint();
}

function setStatusText(t: string): void {
  if (statusEl) statusEl.textContent = t;
}

function updateStatus(): void {
  if (!statusEl) return;
  const parts: string[] = [];
  if (!S.fix) parts.push(sensorsStarted ? "Waiting for GPS…" : "Location off");
  else {
    const warn = S.cfg && S.fix.accuracy > S.cfg.limits.warn_accuracy_m ? " ⚠" : "";
    parts.push(`GPS ±${Math.round(S.fix.accuracy)} m${warn}`);
  }
  const hd = S.heading;
  parts.push(hd.heading === null ? "no compass" : `${compassPoint(hd.heading)} ${Math.round(hd.heading)}°${hd.accuracy !== null ? ` ±${Math.round(hd.accuracy)}°` : ""}`);
  if (!navigator.onLine) parts.push("offline");
  statusEl.textContent = parts.join(" · ");
}

async function updateBadge(): Promise<void> {
  if (!badgeEl) return;
  const items = await listOutbox();
  const waiting = items.filter((i) => !i.failed).length;
  const failed = items.filter((i) => i.failed).length;
  badgeEl.textContent = waiting || failed ? `${waiting ? `${waiting} to upload` : ""}${waiting && failed ? " · " : ""}${failed ? `${failed} need attention` : ""}` : "";
  badgeEl.hidden = !(waiting || failed);
}

function renderMapControls(): void {
  if (!controlsEl) return;
  const areaSel = h("select", { class: "fa-chip fa-area-sel", "aria-label": "Area layer radius",
    title: "Area: every asset within this distance, drawn faintly under the nearest suggestions" },
    ...AREA_RADII.map((r) => h("option", { value: String(r) }, `Area ${r} m`)),
    h("option", { value: "0" }, "Area off")) as HTMLSelectElement;
  areaSel.value = String(S.areaRadius);
  areaSel.addEventListener("change", () => setAreaRadius(Number(areaSel.value)));
  const cal = currentCalibration(S.fix);
  fill(controlsEl,
    cal
      ? h("button", { class: "fa-chip fa-calchip", onclick: () => resetArCalibration(),
          title: "The AR camera view is calibrated. This map is not shifted: the blue dot is raw GPS, the amber ring where the calibration puts you. Tap to reset the calibration." },
          `AR calibrated: ${Math.hypot(cal.east_m, cal.north_m).toFixed(1)} m, ${cal.heading_offset_deg >= 0 ? "+" : "−"}${Math.abs(cal.heading_offset_deg).toFixed(0)}° · Reset`)
      : null,
    S.follow
      ? h("button", { class: "fa-chip on", onclick: () => { S.follow = false; renderMapControls(); }, title: "Drag the map to look around; Re-centre brings you back" }, "Following me")
      : h("button", { class: "fa-chip fa-recentre", onclick: () => { S.follow = true; updateMe(); renderMapControls(); } }, "◎ Re-centre"),
    areaSel,
    h("button", { class: `fa-chip${S.layersOpen ? " on" : ""}`, "aria-expanded": S.layersOpen ? "true" : "false",
      title: "Legend and network facility layers (valves, pump stations, reservoirs)",
      onclick: () => { S.layersOpen = !S.layersOpen; renderMapControls(); } }, "Layers"),
    h("button", { class: `fa-chip${S.rotate ? " on" : ""}`, onclick: () => { S.rotate = !S.rotate; if (!S.rotate) map?.easeTo({ bearing: 0 }); updateMe(); renderMapControls(); } }, S.rotate ? "Map turns with me" : "North up"),
    h("button", { class: `fa-chip${S.basemap === "linz" ? " on" : ""}${S.basemap !== "linz" && !S.ldsAvailable && aerialState.reason ? " fa-chip-off" : ""}`,
      onclick: () => void setBasemap(S.basemap === "linz" ? "streets" : "linz"),
      title: aerialState.reason ? `Aerial unavailable: ${aerialState.reason}` : "LINZ aerial (CC BY 4.0) - the layer offline packs store" },
      S.basemap === "linz" ? "Aerial (LINZ)" : "Streets"),
    S.layersOpen ? layersPopover() : null,
  );
  layoutSheet();
}

/** The map's Layers popover: legend + the Area layer's facility toggles. */
function layersPopover(): HTMLElement {
  const counts: Record<string, number> = {};
  for (const a of S.area?.assets ?? []) counts[a.asset_type] = (counts[a.asset_type] ?? 0) + 1;
  const rows = FACILITY_TOGGLES.map((t) => {
    const cb = h("input", { type: "checkbox" }) as HTMLInputElement;
    cb.checked = S.facilities[t.key];
    cb.addEventListener("change", () => {
      S.facilities[t.key] = cb.checked;
      saveFacilities();
      updateAreaLayer();
    });
    const sym = symbolIcon({ asset_type: t.key, network: t.key === "pump_station" ? "wastewater" : "potable",
      normal_status: t.key === "valve" ? "closed" : null }, 16);
    return h("label", { class: "fa-layers-row" }, cb, sym, `${t.label}${S.area ? ` (${counts[t.key] ?? 0})` : ""}`);
  });
  return h("div", { class: "fa-layers-pop", role: "dialog", "aria-label": "Map layers" },
    h("div", { class: "fa-layers-head" }, h("strong", {}, "Area layer: network facilities"),
      h("button", { class: "fa-link", onclick: () => { S.layersOpen = false; renderMapControls(); } }, "Close")),
    ...rows,
    !S.areaRadius ? h("p", { class: "fa-small fa-muted" }, "The Area layer is off - pick a radius to see facilities around you.") : null,
    S.area?.profile === "public"
      ? h("p", { class: "fa-small fa-muted" }, S.area.area_note
        ?? "Your organisation sees the public layers: normally-closed shut valves and reservoirs (no register pump stations).")
      : null,
    legendEl(true),
  );
}

function render(): void {
  if (!root) return;
  const user = getState().currentUser;
  const banner = S.cfg?.safety_banner || "Positions are approximate. Not a substitute for beforeUdig or a certified locator.";
  const advice = installAdvice();
  statusEl = h("span", { class: "fa-status" });
  badgeEl = h("span", { class: "fa-badge", hidden: true, onclick: () => setView("mine") });
  viewEl = h("div", { class: "fa-view-body" });
  handleEl = sheetHandle();
  sheetEl = h("div", { class: "fa-view" }, handleEl, viewEl);
  navEl = h("nav", { class: "fa-nav" });
  controlsEl = h("div", { class: "fa-map-controls" });
  hintEl = h("div", { class: "fa-map-hint", role: "status", hidden: true });
  const mapEl = h("div", { class: "fa-map" });
  fill(root, 
    h("header", { class: "fa-header" },
      h("strong", {}, S.ws === "operations" ? "Operations" : "Construction"),
      statusEl,
      badgeEl,
      h("button", { class: "fa-exit", onclick: () => closeFieldApp(), title: "Back to the main app" }, "✕"),
    ),
    safetyLine(banner),
    // row #40: what is waiting to upload, the last error and "Send now", on every view
    uploadBadge({ className: "fa-upq-strip", onOpen: () => setView("mine") }),
    advice
      ? h("div", { class: "fa-install" },
          h("strong", {}, advice.title), h("p", {}, advice.body),
          advice.canPrompt ? h("button", { class: "fa-btn", onclick: () => void promptInstall().then(render) }, "Install") : null,
          h("button", { class: "fa-link", onclick: () => { dismissInstallAdvice(); render(); } }, "Not now"))
      : null,
    !sensorsStarted
      ? h("div", { class: "fa-start" },
          h("p", {}, `Hi ${user?.display_name ?? ""}. WRAP needs your location and compass to find the assets around you. Photos record where you were and which way you faced.`),
          h("button", { class: "fa-btn fa-big", onclick: () => void startSensors() }, "Start location + compass"))
      : null,
    h("div", { class: "fa-body" }, h("div", { class: "fa-map-wrap" }, mapEl, controlsEl, hintEl), sheetEl),
    navEl,
  );
  remountGuide(root);
  initMapInto(mapEl);
  renderMapControls();
  renderMapHint();
  renderNav();
  renderView();
  updateStatus();
  void updateBadge();
}

/** The safety banner (2026-10-06: it took three lines over every view): one
 * slim line that expands on tap. Never removable. */
function safetyLine(text: string): HTMLElement {
  const el = h("button", { class: "fa-safety", role: "note", "aria-expanded": "false", "aria-label": `Safety: ${text}` }, "⚠ ", text);
  el.addEventListener("click", () => {
    const open = !el.classList.contains("open");
    el.classList.toggle("open", open);
    el.setAttribute("aria-expanded", open ? "true" : "false");
  });
  return el;
}

function initMapInto(el: HTMLElement): void {
  if (map) {
    el.replaceWith(map.getContainer());
    map.getContainer().classList.add("fa-map");
    window.setTimeout(() => map?.resize(), 50);
    return;
  }
  initMap(el);
}

function renderNav(): void {
  if (!navEl) return;
  fill(navEl, 
    ...VIEWS[S.ws].map((v) =>
      h("button", { class: `fa-tab${S.view === v.key ? " active" : ""}`, onclick: () => setView(v.key) }, h("span", { class: "fa-ico" }, v.icon), h("span", {}, v.label)),
    ),
  );
}

function setView(v: View): void {
  if (S.view === "ar" && v !== "ar") stopAr();
  S.view = v;
  renderNav();
  renderView();
  if (v === "jobs") void loadJobs();
  if (v === "projects" || (v === "msqa" && !S.projects.length)) void loadProjects();
  if (v === "near" || v === "radar") maybeQueryNearby(true);
  maybeQueryArea(); // paused while in AR
}

let arEl: HTMLElement | null = null;

function viewAr(): HTMLElement {
  if (!sensorsStarted) {
    return h("p", { class: "fa-muted" }, "Tap “Start location + compass” first: the camera view needs both.");
  }
  arEl = h("div", { class: "fa-ar-host" });
  mountAr(arEl, {
    // the filtered position (GPS + steps + calibration anchors, track.ts); raw GPS until it has one
    getFix: () => {
      const t = trackedFix();
      if (t) return { lat: t.lat, lon: t.lon, accuracy: t.accuracy, gpsAccuracy: t.gpsAccuracy };
      return S.fix ? { lat: S.fix.lat, lon: S.fix.lon, accuracy: S.fix.accuracy, gpsAccuracy: S.fix.accuracy } : null;
    },
    selected: () => S.selected,
    onSelect: (id) => {
      S.selected = id;
    },
    fallbackToRadar: (why) => {
      if (S.view === "ar") {
        showToast(`${why} Showing the radar.`);
        setView("radar");
      }
    },
    // the AR toolbar's "Back to the map" (2026-10-06): the camera view hides the header and tabs
    exit: () => {
      if (S.view === "ar") setView("near");
    },
    // AR scan mode (2026-10-14): scans are stored with one of the organisation's projects
    projects: () => S.projects.map((p) => ({ id: p.id, name: p.name })),
    projectId: () => S.project,
    rawFix: () => (S.fix ? { lat: S.fix.lat, lon: S.fix.lon, accuracy: S.fix.accuracy, at: S.fix.at } : null),
  });
  if (!S.projects.length) void loadProjects();
  return arEl;
}

function renderView(): void {
  if (!viewEl) return;
  root?.classList.toggle("fa-map-small", S.view === "capture" || S.view === "mine" || S.view === "projects");
  // MSQA forms take the whole screen (no map): a form is long and the keyboard is up
  root?.classList.toggle("fa-msqa-mode", S.view === "msqa");
  root?.classList.toggle("fa-ar-mode", S.view === "ar");
  // the AR view owns the camera: never rebuild it on a data refresh
  if (S.view === "ar" && arEl && viewEl.contains(arEl)) {
    layoutSheet();
    return;
  }
  // an open MSQA form keeps its DOM (inputs, focus, scroll) across data refreshes
  if (S.view === "msqa" && msqaEditing() && viewEl.firstElementChild?.classList.contains("msqa-editor")) {
    const keep = viewMsqa(msqaHost);
    if (viewEl.firstElementChild === keep) return;
  }
  const fn = { near: viewNear, radar: viewRadar, ar: viewAr, capture: viewCapture, jobs: viewJobs, projects: viewProjects, mine: viewMine,
               msqa: () => viewMsqa(msqaHost) }[S.view];
  fill(viewEl, fn());
  updateMapAssets();
  renderMapHint();
  updatePeek();
  layoutSheet();
  window.setTimeout(() => map?.resize(), 30);
}

// --- the bottom sheet (phones; sheet.ts) ----------------------------------------

const SHEET_VIEWS: Partial<Record<View, SheetState>> = { near: "half", radar: "full", jobs: "half" };
const SHEET_KEY = "wrap.field.sheet.";
const phoneMq = typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 899px)") : null;
let sheetEl: HTMLElement | null = null;
let handleEl: HTMLButtonElement | null = null;
let sheetHeightsNow: SheetHeights | null = null;

function sheetOn(): boolean {
  return !!phoneMq?.matches && S.view in SHEET_VIEWS;
}

function sheetStateFor(v: View): SheetState {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SHEET_KEY + v);
  } catch {
    /* storage unavailable */
  }
  return storedState(raw, SHEET_VIEWS[v] ?? "half");
}

function saveSheetState(v: View, s: SheetState): void {
  try {
    localStorage.setItem(SHEET_KEY + v, s);
  } catch {
    /* storage unavailable */
  }
}

/** The peek row: how many, and the nearest (what the collapsed sheet shows). */
function sheetPeek(): string {
  if (S.view === "jobs") return S.jobs.length ? `${S.jobs.length} live job${S.jobs.length === 1 ? "" : "s"} nearby` : "Live jobs near you";
  const cands = candidates();
  if (!S.fix) return sensorsStarted ? "Waiting for a GPS fix…" : "Nearby assets";
  if (!cands.length) return S.nearby ? "No assets found nearby" : "Finding nearby assets…";
  const c = cands[0];
  return `${cands.length} nearby · nearest: ${c.label}, ${directionText(c.distance_m, c.bearing_deg, S.heading.heading)}`;
}

/** The px the sheet must leave free at the top of the body: the map
 * controls row (not the Layers popover, which scrolls instead) + a margin. */
function controlsReserve(): number {
  if (!controlsEl) return 64;
  let bottom = 0;
  for (const ch of Array.from(controlsEl.children) as HTMLElement[]) {
    if (ch.classList.contains("fa-layers-pop")) continue;
    bottom = Math.max(bottom, ch.offsetTop + ch.offsetHeight);
  }
  return controlsEl.offsetTop + bottom + 8;
}

/** Size the sheet for its state (or a drag height), and keep everything on
 * the map clear of it: the hint, the attribution, the follow-me centre and
 * the Layers popover's height. */
function layoutSheet(dragH?: number): void {
  if (!root) return;
  const on = sheetOn();
  root.classList.toggle("fa-sheet-mode", on);
  // the "Guide me" card sits over the map's top: the controls go below it
  const guide = root.classList.contains("fa-guiding") ? root.querySelector<HTMLElement>(".fa-guide") : null;
  const wrap = controlsEl?.parentElement;
  if (controlsEl && wrap) {
    const gb = guide && !guide.hidden ? guide.getBoundingClientRect().bottom - wrap.getBoundingClientRect().top : 0;
    controlsEl.style.top = gb > 0 ? `${Math.round(gb + 6)}px` : "";
  }
  if (!on || !sheetEl || !handleEl) {
    root.style.removeProperty("--sheet-h");
    if (sheetEl) sheetEl.style.height = "";
    map?.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
    return;
  }
  const body = sheetEl.parentElement as HTMLElement;
  const hs = sheetHeights(body.clientHeight, controlsReserve());
  sheetHeightsNow = hs;
  const state = sheetStateFor(S.view);
  const hpx = dragH !== undefined ? clampHeight(dragH, hs) : hs[state];
  sheetEl.style.height = `${hpx}px`;
  sheetEl.dataset.state = state;
  root.style.setProperty("--sheet-h", `${hpx}px`);
  root.style.setProperty("--controls-h", `${controlsReserve()}px`);
  root.style.setProperty("--pop-max", `${Math.max(160, body.clientHeight - hpx - controlsReserve() - 8)}px`);
  handleEl.setAttribute("aria-expanded", state === "collapsed" ? "false" : "true");
  handleEl.setAttribute("aria-label", `${sheetPeek()}. List ${state === "collapsed" ? "collapsed" : state === "half" ? "half open" : "fully open"}: tap to ${state === "collapsed" ? "open" : "collapse"}, drag or use the arrow keys to resize.`);
  if (dragH === undefined && map) {
    map.setPadding({ top: Math.min(controlsReserve(), body.clientHeight / 3), bottom: hpx, left: 0, right: 0 });
    if (S.follow) updateMe();
  }
}

function setSheetState(s: SheetState): void {
  saveSheetState(S.view, s);
  sheetEl?.classList.remove("dragging");
  layoutSheet();
}

function updatePeek(): void {
  const t = handleEl?.querySelector(".fa-sheet-peek");
  if (t) t.textContent = sheetPeek();
}

/** The drag handle: drag to resize (snaps, flings), tap to toggle, arrow
 * keys to step. Pointer events only on the handle, so the list scrolls. */
function sheetHandle(): HTMLButtonElement {
  const btn = h("button", { class: "fa-sheet-handle", type: "button" },
    h("span", { class: "fa-sheet-grip", "aria-hidden": "true" }),
    h("span", { class: "fa-sheet-peek" }, sheetPeek())) as HTMLButtonElement;
  let start: { y: number; h: number; moved: boolean } | null = null;
  const samples: { y: number; t: number }[] = [];
  btn.addEventListener("pointerdown", (e) => {
    if (!sheetEl || !sheetOn()) return;
    start = { y: e.clientY, h: sheetEl.getBoundingClientRect().height, moved: false };
    samples.length = 0;
    samples.push({ y: e.clientY, t: e.timeStamp });
    btn.setPointerCapture(e.pointerId);
  });
  btn.addEventListener("pointermove", (e) => {
    if (!start || !sheetEl) return;
    const dy = e.clientY - start.y;
    if (!start.moved && Math.abs(dy) < 6) return;
    start.moved = true;
    sheetEl.classList.add("dragging");
    samples.push({ y: e.clientY, t: e.timeStamp });
    while (samples.length > 2 && e.timeStamp - samples[0].t > 120) samples.shift();
    layoutSheet(start.h - dy);
  });
  const end = (e: PointerEvent) => {
    if (!start || !sheetEl) return;
    const s0 = start;
    start = null;
    sheetEl.classList.remove("dragging");
    if (!s0.moved) return; // a tap: the click handler toggles
    const first = samples[0];
    const v = first && e.timeStamp > first.t ? -(e.clientY - first.y) / (e.timeStamp - first.t) : 0;
    const hs = sheetHeightsNow;
    if (hs) setSheetState(snapState(clampHeight(s0.h - (e.clientY - s0.y), hs), hs, v));
    btn.dataset.dragged = "1"; // swallow the click that follows a drag
  };
  btn.addEventListener("pointerup", end);
  btn.addEventListener("pointercancel", end);
  btn.addEventListener("click", () => {
    if (btn.dataset.dragged) {
      delete btn.dataset.dragged;
      return;
    }
    if (sheetOn()) setSheetState(tapState(sheetStateFor(S.view)));
  });
  btn.addEventListener("keydown", (e) => {
    if (!sheetOn() || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    setSheetState(stepState(sheetStateFor(S.view), e.key === "ArrowUp" ? 1 : -1));
  });
  return btn;
}

function agoText(at: number): string {
  const s = Math.round((Date.now() - at) / 1000);
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function roadLine(): HTMLElement | null {
  const err = S.nearbyError;
  if (err) {
    const why = err.kind === "offline" ? "No signal" : err.kind === "slow" ? "The server is slow to answer" : `The server couldn't answer (${err.message})`;
    const showing = S.offlineCands
      ? S.offlineCands.length ? "Using cached assets (experimental)." : "No cached assets for this spot."
      : S.nearby ? "Showing the last list from the server." : "";
    const last = S.lastNearbyOk ? ` Last update ${agoText(S.lastNearbyOk)}.` : "";
    return h("p", { class: "fa-road offline" }, `${why} - retrying…${last} ${showing}`);
  }
  if (S.offlineCands) {
    return h("p", { class: "fa-road offline" }, S.offlineCands.length ? "Using cached assets (experimental)." : "No cached assets for this spot.");
  }
  const r = S.nearby?.road;
  if (S.nearby && !S.nearby.candidates.length) {
    return h("p", { class: "fa-road" }, `${r?.sentence ? r.sentence + " " : ""}No assets in our data within ${Math.round(S.nearby.search_radius_m)} m of here - this area may not be covered.`);
  }
  if (!r?.sentence) return null;
  return h("p", { class: `fa-road conf-${r.confidence}` }, r.sentence);
}

function candidateRow(c: Candidate): HTMLElement {
  const sel = c.asset_id === S.selected;
  return h("li", { class: `fa-cand${sel ? " sel" : ""}`, "data-asset-id": c.asset_id, onclick: () => selectAsset(c.asset_id) },
    symbolIcon(c, 18),
    h("span", { class: "fa-cand-main" },
      h("strong", {}, c.label), h("small", {}, `${c.asset_id} · ${directionText(c.distance_m, c.bearing_deg, S.heading.heading)}${c.along_road ? " · along this road" : ""}${c.in_project ? " · in this project" : ""}`)),
    h("span", { class: "fa-arrow", style: `transform: rotate(${arrowRotation(c.bearing_deg, S.heading.heading)}deg)` }, "↑"),
  );
}

function selectAsset(id: string): void {
  S.selected = S.selected === id ? null : id;
  renderView();
}

function selectedCandidate(): Candidate | undefined {
  if (!S.selected) return undefined;
  const c = candidates().find((x) => x.asset_id === S.selected);
  if (c) return c;
  const a = S.area?.assets.find((x) => x.asset_id === S.selected);
  return a ? areaCandidate(a) : undefined;
}

function viewNear(): HTMLElement {
  const cands = candidates();
  const sel = selectedCandidate();
  const projectNote = S.ws === "construction" && S.project ? h("p", { class: "fa-muted" }, `Site: ${S.projects.find((p) => p.id === S.project)?.name ?? S.project}`) : null;
  return h("div", {},
    projectNote,
    roadLine(),
    !S.fix ? h("p", { class: "fa-muted" }, sensorsStarted ? "Waiting for a GPS fix…" : "Start location to see nearby assets.") : null,
    h("ul", { class: "fa-list" }, ...cands.slice(0, 8).map(candidateRow)),
    sel ? candidateCard(sel) : null,
    sel ? assetActions(sel) : cands.length ? h("p", { class: "fa-muted" }, "Tap the asset you are working on (here or on the map) to see what it is. These are suggestions - you confirm.") : null,
    cands.length ? legendEl(true) : null,
    S.areaRadius && S.area
      ? h("p", { class: "fa-muted fa-small" }, `Map: bold = the nearest suggestions; faint = all ${S.area.n} assets within ${Math.round(S.area.radius_m)} m (Area). Drag or pinch the map to look around - tap any of them to identify it.`)
      : null,
    S.fix ? h("button", { class: "fa-link", onclick: () => retryNow() }, "Refresh") : null,
  );
}

/** The identify card for a list / map / radar selection. */
function candidateCard(c: Candidate): HTMLElement {
  return identifyCard({ ...c, direction: directionText(c.distance_m, c.bearing_deg, S.heading.heading) }, [],
    () => { S.selected = null; renderView(); });
}

/** Field photos on the selected asset (2026-10-09, assetPhotos.ts): a
 * compact thumbnail strip, blurred shared copies. The card re-renders on
 * every GPS update, so the last answer per asset is drawn synchronously
 * and the (one-minute cached) fetch only redraws when it changed. */
const assetPhotoData = new Map<string, AssetPhotos>();
function assetPhotosStrip(assetId: string): HTMLElement {
  const box = h("div", { class: "fa-asset-photos" });
  const draw = (d: AssetPhotos) =>
    fill(box, h("h4", {}, `Field photos (${d.n_photos})`), renderAssetPhotos(d, { compact: true }));
  const have = assetPhotoData.get(assetId);
  if (have) draw(have);
  void fetchAssetPhotos(assetId)
    .then((d) => {
      if (d === have) return;
      assetPhotoData.set(assetId, d);
      draw(d);
    })
    .catch(() => {
      if (!have) box.remove();
    });
  return box;
}

function assetActions(c: Candidate): HTMLElement {
  const hist = h("div", { class: "fa-history" });
  return h("div", { class: "fa-actions" },
    assetPhotosStrip(c.asset_id),
    h("button", { class: "fa-btn fa-big", onclick: () => setView("capture") }, S.ws === "operations" ? "Capture / triage this" : "Capture against this"),
    h("button", { class: "fa-btn", onclick: () => setView("radar") }, "Show on radar"),
    guideButtons(c),
    S.cfg?.fault_history_available && S.ws === "operations"
      ? h("button", { class: "fa-btn", onclick: () => void loadHistory(c.asset_id, hist) }, "Fault history")
      : null,
    hist,
  );
}

async function loadHistory(assetId: string, into: HTMLElement): Promise<void> {
  into.textContent = "Loading…";
  try {
    const hst = await fieldApi.history(assetId);
    const bm = hst.break_model;
    fill(into, 
      bm ? h("p", {}, `Break model: grade ${bm.failure_likelihood_grade ?? "—"}, expected failures next year ${bm.pred_failures_next_yr ?? "—"}`) : null,
      h("p", {}, `${hst.n_maximo} Maximo fault(s), ${hst.n_break_model} break-model event(s)`),
      h("ul", { class: "fa-mini" },
        ...hst.maximo_events.slice(0, 10).map((e) => h("li", {}, `${e.date_resolved ?? ""} ${e.fault_type ?? ""} (${e.fault_class ?? ""})`)),
        ...hst.break_model_events.slice(0, 10).map((e) => h("li", {}, `${e.date ?? ""} ${e.failure_component ?? ""} - ${e.crew_problem ?? ""}`)),
      ),
    );
  } catch {
    into.textContent = "No fault history available for this asset (or your organisation doesn't have access to it).";
  }
}

// --- radar (2026-10-08: real pipe geometry, radar.ts) -----------------------

const RADAR_RANGES = [20, 40, 80, 150];
let radarRange: number | null = null; // null = automatic (fits the nearest suggestions)

/** Everything the radar can draw: the Area layer (every asset within its
 * radius, with full pipe paths) plus the ranked suggestions (which carry
 * paths too), one entry per asset. Same array object while the data is the
 * same, so radar.ts reprojects only when something changed. */
let radarAssetsCache: { area: AreaResult | null; cands: Candidate[]; out: RadarAsset[] } | null = null;
function radarAssets(): RadarAsset[] {
  const cands = candidates();
  if (radarAssetsCache && radarAssetsCache.area === S.area && radarAssetsCache.cands === cands) return radarAssetsCache.out;
  const seen = new Map<string, RadarAsset>();
  const add = (a: { asset_id: string; asset_type: string; network?: string | null; water_type?: string | null; label: string;
    path?: [number, number][]; lon?: number; lat?: number; diameter_mm?: number | null; normal_status?: string | null }) => {
    if (seen.has(a.asset_id) || !facilityShown(a.asset_type)) return;
    seen.set(a.asset_id, { id: a.asset_id, type: a.asset_type, network: assetNetwork(a), water_type: a.water_type, label: a.label,
      path: a.path ?? null, lon: a.lon ?? null, lat: a.lat ?? null, diameter_mm: a.diameter_mm ?? null, normal_status: a.normal_status ?? null });
  };
  for (const c of cands) add(c);
  for (const a of S.area?.assets ?? []) add(a);
  // the open project's pipes (they may lie beyond the Area radius)
  for (const f of projectData.features) {
    const id = String(f.properties?.asset_id ?? "");
    if (id && f.geometry.type === "LineString" && !seen.has(id)) {
      seen.set(id, { id, type: "pipe", network: null, label: id, path: f.geometry.coordinates as [number, number][] });
    }
  }
  const out = [...seen.values()];
  radarAssetsCache = { area: S.area, cands, out };
  return out;
}

function autoRadarRange(cands: Candidate[]): number {
  const d = (cands[Math.min(4, cands.length - 1)]?.distance_m ?? 30) * 1.3;
  return RADAR_RANGES.find((r) => r >= d) ?? RADAR_RANGES[RADAR_RANGES.length - 1];
}

function viewRadar(): HTMLElement {
  const canvas = h("canvas", { class: "fa-radar" });
  const sel = selectedCandidate();
  const cands = candidates();
  const range = () => radarRange ?? autoRadarRange(candidates());
  const fixNow = () => {
    const t = trackedFix();
    return t ? { lat: t.lat, lon: t.lon } : S.fix ? { lat: S.fix.lat, lon: S.fix.lon } : null;
  };
  startRadar(canvas, () => ({
    assets: radarAssets(),
    state: {
      origin: fixNow(),
      heading: S.heading.heading,
      accuracy: S.fix?.accuracy ?? null,
      selected: S.selected,
      rangeM: range(),
      emphasis: projectIds(),
      target: guideTarget(),
    },
  }));
  // tap a pipe or a symbol on the radar to see what it is
  canvas.addEventListener("click", (ev) => {
    const rect = canvas.getBoundingClientRect();
    const id = radarHitTest(ev.clientX - rect.left, ev.clientY - rect.top);
    if (id) {
      S.selected = id;
      renderView();
    }
  });
  const zoom = h("div", { class: "fa-chips fa-radar-zoom", role: "group", "aria-label": "Radar range" },
    ...RADAR_RANGES.map((r) => h("button", { class: `fa-chip${range() === r ? " on" : ""}`, "aria-pressed": range() === r ? "true" : "false",
      onclick: () => { radarRange = r; renderView(); } }, `${r} m`)),
    h("button", { class: `fa-chip${radarRange === null ? " on" : ""}`, onclick: () => { radarRange = null; renderView(); } }, "Auto"));
  const noArea = !S.areaRadius;
  return h("div", { class: "fa-radar-wrap" },
    canvas,
    zoom,
    sel
      ? h("div", { class: "fa-direction" },
          h("span", { class: "fa-arrow big", style: `transform: rotate(${arrowRotation(sel.bearing_deg, S.heading.heading)}deg)` }, "↑"),
          h("strong", {}, directionText(sel.distance_m, sel.bearing_deg, S.heading.heading)), h("small", {}, sel.label))
      : h("p", { class: "fa-muted" }, "Tap a pipe or a symbol (or pick from the list) to see what it is."),
    sel ? candidateCard(sel) : null,
    sel ? guideButtons(sel) : null,
    legendEl(true),
    h("p", { class: "fa-muted fa-small" },
      `Up is the way you're facing. Pipes are drawn where the map has them, line width by diameter. The blue disc is your GPS accuracy (±${Math.round(S.fix?.accuracy ?? 0)} m).` +
      (noArea ? " Only the nearest suggestions are drawn: turn the Area layer on (map Layers) to see every pipe around you." : "")),
    h("button", { class: "fa-btn", onclick: () => setView("ar") }, "Camera view (AR, indicative only)"),
    h("ul", { class: "fa-list" }, ...cands.slice(0, 6).map(candidateRow)),
  );
}

/** The open project's asset ids (emphasised on the radar). */
let projectIdsCache: { data: GeoJSON.FeatureCollection; ids: Set<string> } | null = null;
function projectIds(): Set<string> | null {
  if (!S.project || !projectData.features.length) return null;
  if (projectIdsCache?.data !== projectData) {
    projectIdsCache = { data: projectData, ids: new Set(projectData.features.map((f) => String(f.properties?.asset_id ?? ""))) };
  }
  return projectIdsCache.ids;
}

// --- "Guide me" + "Directions" (2026-10-08, guide.ts / directions.ts) --------

function guideHost() {
  return {
    getFix: () => {
      const t = trackedFix();
      if (t) return { lat: t.lat, lon: t.lon, accuracy: t.gpsAccuracy ?? t.accuracy };
      return S.fix ? { lat: S.fix.lat, lon: S.fix.lon, accuracy: S.fix.accuracy } : null;
    },
    getHeading: () => S.heading.heading,
    onArrive: (assetId: string) => {
      // highlight it everywhere: list, map, radar, AR
      S.selected = assetId;
      showToast("You're there - the asset is highlighted. Positions are approximate: check on the ground.");
      try {
        navigator.vibrate?.(120);
      } catch {
        /* no vibration */
      }
      renderView();
    },
    onChange: () => radarDirty(),
    onStop: () => {
      root?.classList.remove("fa-guiding");
      layoutSheet();
    },
  };
}

function beginGuide(spec: { kind: "asset" | "project"; id: string; label: string; items: GuideItem[] }): void {
  if (!root) return;
  if (!spec.items.length) {
    showToast("That has no location in our data to guide you to.");
    return;
  }
  if (!sensorsStarted) void startSensors();
  root.classList.add("fa-guiding");
  startGuide(spec, guideHost(), root);
  radarDirty();
  requestAnimationFrame(() => layoutSheet());
}

/** Guide to one asset: from what's already on the phone (suggestions, Area,
 * the open project) - works offline - else /api/field/locate. */
async function guideToAsset(assetId: string, label?: string): Promise<void> {
  const c = candidates().find((x) => x.asset_id === assetId) ?? S.area?.assets.find((x) => x.asset_id === assetId);
  if (c && ((c.path && c.path.length) || (typeof c.lon === "number" && typeof c.lat === "number"))) {
    beginGuide({ kind: "asset", id: assetId, label: label ?? c.label, items: [{ id: assetId, path: c.path ?? null, lon: c.lon, lat: c.lat, label: c.label }] });
    return;
  }
  const pf = projectData.features.find((f) => String(f.properties?.asset_id ?? "") === assetId);
  if (pf && pf.geometry.type === "LineString") {
    beginGuide({ kind: "asset", id: assetId, label: label ?? assetId, items: [{ id: assetId, path: pf.geometry.coordinates as [number, number][] }] });
    return;
  }
  try {
    const a = await fieldApi.locate(assetId);
    beginGuide({ kind: "asset", id: assetId, label: label ?? a.label ?? assetId,
      items: [{ id: assetId, path: a.path ?? null, lon: a.lon, lat: a.lat, label: a.label }] });
  } catch (e) {
    showToast(failureKind(e) === "offline" ? "Offline, and that asset isn't saved on this phone yet." : "Couldn't find where that asset is.");
  }
}

/** Guide to a project's pipes (nearest one first; "Next asset" steps). */
async function guideToProject(projectId: string): Promise<void> {
  try {
    const site = await fieldApi.projectSite(projectId);
    const items: GuideItem[] = site.pipes.filter((p) => p.path?.length).map((p) => ({ id: p.asset_id, path: p.path, label: p.asset_id }));
    beginGuide({ kind: "project", id: projectId, label: site.project.name, items });
  } catch (e) {
    showToast(failureKind(e) === "offline" ? "Offline, and this project's pipes aren't saved on this phone yet (open it once with signal)." : "Couldn't load the project's pipes.");
  }
}

/** A maps-app link for a candidate (its nearest point). */
function directionsLinkFor(c: Candidate): string | null {
  const lat = c.lat ?? null;
  const lon = c.lon ?? null;
  let p: [number, number] | null = lat !== null && lon !== null ? [lon, lat] : null;
  if (!p && c.path?.length) p = c.path[Math.floor(c.path.length / 2)];
  if (!p) return null;
  const far = c.distance_m > 1500;
  return preferredDirectionsLink(navigator.userAgent, p[1], p[0], far ? "driving" : "walking", c.label);
}

function guideButtons(c: Candidate): HTMLElement {
  const href = directionsLinkFor(c);
  return h("div", { class: "fa-row fa-guide-entry" },
    h("button", { class: "fa-btn", onclick: () => void guideToAsset(c.asset_id, c.label) }, "➤ Guide me"),
    href ? h("a", { class: "fa-btn", href, target: "_blank", rel: "noopener" }, "Directions in Maps") : null);
}

/** From anywhere in the app (directions.ts "Guide me": asset details, search,
 * My work, a project): open a field workspace and guide. */
async function guideFromOutside(d: FieldGuideDetail): Promise<void> {
  const user = getState().currentUser;
  const fieldWs = workspaceChoices(user).filter((w): w is FieldWorkspace => isFieldWorkspace(w));
  if (!fieldWs.length) {
    showToast("Guide me is part of the field app (Operations / Construction workspaces).");
    return;
  }
  const cur = currentWorkspace();
  const ws = isFieldWorkspace(cur) ? cur : fieldWs[0];
  if (!isFieldWorkspace(cur) && !switchWorkspace(ws)) return;
  if (!S.open) await openFieldApp(ws);
  if (d.project_id) {
    S.project = d.project_id;
    await guideToProject(d.project_id);
  } else if (d.asset_id) {
    await guideToAsset(d.asset_id);
  }
}

// ---------------------------------------------------------------------------
// Capture
// ---------------------------------------------------------------------------

async function downscale(file: File, longEdge = 2560): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
    const s = Math.min(1, longEdge / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s);
    c.height = Math.round(bmp.height * s);
    c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
    return await new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("encode"))), "image/jpeg", 0.85));
  } catch {
    return file;
  }
}

function select(name: string, options: { key: string | number; label: string }[], value: string | null, blank = "—"): HTMLSelectElement {
  const s = h("select", { name, class: "fa-input" }, h("option", { value: "" }, blank), ...options.map((o) => h("option", { value: String(o.key) }, o.label)));
  s.value = value ?? "";
  return s;
}

function viewCapture(): HTMLElement {
  const cfg = S.cfg;
  if (!cfg) return h("p", {}, "Loading…");
  const kinds = Object.entries(cfg.forms).filter(([, f]) => f.workspace === S.ws);
  const sel = selectedCandidate();
  const form = h("form", { class: "fa-form" });
  const kindSel = select("form_kind", kinds.map(([k, f]) => ({ key: k, label: f.label })), kinds[0]?.[0] ?? "ops_triage", "Choose…");
  const extra = h("div", { class: "fa-extra" });
  const renderExtra = () => {
    const f = cfg.forms[kindSel.value];
    fill(extra, 
      ...(f?.fields ?? []).map((fd) => {
        let input: HTMLElement;
        if (fd.type === "select") {
          const sel = select(`f_${fd.key}`, (fd.options ?? []).map((o) => ({ key: o, label: fd.option_labels?.[o] ?? o.replace(/_/g, " ") })), null);
          if (!fd.other) input = sel;
          else {
            // row #49: "Other (specify)" asks what it is; the box is only in the form (and sent) while Other is picked
            const spec = h("input", { type: "text", name: `f_${fd.key}_other`, class: "fa-input", maxlength: 200, required: true,
              placeholder: "Say what it is", "aria-label": `${fd.label}: other` });
            const slot = h("div", { class: "fa-other" });
            sel.addEventListener("change", () => (sel.value === "other" ? fill(slot, spec) : fill(slot)));
            input = h("div", {}, sel, slot);
          }
        } else if (fd.type === "bool") input = h("input", { type: "checkbox", name: `f_${fd.key}`, class: "fa-check" });
        else if (fd.type === "textarea") input = h("textarea", { name: `f_${fd.key}`, class: "fa-input", rows: 2 });
        else input = h("input", { type: fd.type === "number" ? "number" : "text", inputmode: fd.type === "number" ? "decimal" : undefined, step: "any", name: `f_${fd.key}`, class: "fa-input" });
        return h("label", { class: fd.type === "bool" ? "fa-row-check" : "fa-field" }, h("span", {}, fd.label), input);
      }),
    );
  };
  kindSel.addEventListener("change", renderExtra);
  renderExtra();

  let cands = candidates().slice(0, Math.max(5, Math.min(8, candidates().length)));
  // an asset tapped in the faint Area layer is offered too
  if (sel && !cands.some((c) => c.asset_id === sel.asset_id)) cands = [sel, ...cands];
  const assetList = h("div", { class: "fa-asset-pick" },
    ...cands.map((c) =>
      h("label", { class: "fa-row-check" },
        h("input", { type: "radio", name: "asset_id", value: c.asset_id, checked: c.asset_id === S.selected }),
        h("span", {}, `${c.label} - ${Math.round(c.distance_m)} m`, h("small", {}, ` ${c.asset_id}${c.in_project ? " · in this project" : ""}`))),
    ),
    h("label", { class: "fa-row-check" }, h("input", { type: "radio", name: "asset_id", value: "", checked: !S.selected }), h("span", {}, "Not listed / not sure")),
  );
  const typeSel = select("asset_type", cfg.asset_types, sel?.asset_type ?? null);
  assetList.addEventListener("change", () => {
    const v = (form.querySelector("input[name=asset_id]:checked") as HTMLInputElement | null)?.value;
    const c = cands.find((x) => x.asset_id === v);
    if (c) typeSel.value = c.asset_type;
  });
  const projSel = select("project_id", S.projects.map((p) => ({ key: p.id, label: p.name })), S.project, "No project");

  const thumbs = h("div", { class: "fa-thumbs" });
  const renderThumbs = () =>
    fill(thumbs, 
      ...S.photos.map((p, i) =>
        h("figure", {}, h("img", { src: p.url, alt: `photo ${i + 1}` }),
          h("button", { type: "button", class: "fa-x", onclick: () => { URL.revokeObjectURL(p.url); S.photos.splice(i, 1); renderThumbs(); } }, "✕"))),
    );
  renderThumbs();
  const fullRes = h("input", { type: "checkbox", name: "full_res", class: "fa-check" });
  const fileIn = h("input", { type: "file", accept: "image/*", capture: "environment", class: "fa-file" });
  fileIn.addEventListener("change", async () => {
    // position + heading recorded NOW, at the moment of capture
    const hd = getHeading();
    if (!S.captureFix && S.fix) S.captureFix = { ...S.fix };
    if (!S.captureHeading) S.captureHeading = hd;
    for (const f of Array.from(fileIn.files ?? [])) {
      if (S.photos.length >= cfg.limits.max_photos_per_capture) break;
      const blob = !(fullRes as HTMLInputElement).checked && f.size > 3 * 1024 * 1024 ? await downscale(f) : f;
      S.photos.push({ blob, url: URL.createObjectURL(blob), heading: hd.heading });
    }
    fileIn.value = "";
    renderThumbs();
  });

  const accWarn = S.fix && S.fix.accuracy > cfg.limits.warn_accuracy_m
    ? h("p", { class: "fa-warn" }, `GPS accuracy is ±${Math.round(S.fix.accuracy)} m - move into the open if you can, or note the location in the notes.`)
    : null;

  fill(form,
    accWarn,
    h("label", { class: "fa-field" }, h("span", {}, "Record type"), kindSel),
    h("div", { class: "fa-photo-row" },
      h("label", { class: "fa-btn fa-big fa-camera" }, "📷 Take photo", fileIn),
      h("label", { class: "fa-row-check small" }, fullRes, h("span", {}, "Full resolution"))),
    thumbs,
    h("fieldset", {}, h("legend", {}, "Which asset? (nearest first - you confirm)"), assetList),
    h("label", { class: "fa-field" }, h("span", {}, "Asset type"), typeSel),
    h("label", { class: "fa-field" }, h("span", {}, "Fault / issue"), select("fault_type", cfg.fault_types, S.ws === "construction" ? "no_fault" : null)),
    S.ws === "operations" ? h("label", { class: "fa-field" }, h("span", {}, "Failed component (if known)"), select("failure_component", cfg.failure_components, null)) : null,
    h("label", { class: "fa-field" }, h("span", {}, "Severity"), select("severity", cfg.severities, null)),
    S.projects.length || S.ws === "construction" ? h("label", { class: "fa-field" }, h("span", {}, "Project"), projSel) : null,
    extra,
    h("label", { class: "fa-field" }, h("span", {}, "Notes"), h("textarea", { name: "notes", class: "fa-input", rows: 3, maxlength: 4000 })),
    h("button", { type: "submit", class: "fa-btn fa-big fa-primary" }, "Save capture"),
    h("p", { class: "fa-muted" }, "Saved on this phone first, then uploaded when there's signal."),
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void submitCapture(form, kindSel.value);
  });
  return form;
}

async function submitCapture(form: HTMLFormElement, kind: string): Promise<void> {
  const fd = new FormData(form);
  if (!S.photos.length && !(fd.get("notes") as string)?.trim()) {
    showToast("Add a photo or a note first.");
    return;
  }
  // row #49: "Other" needs what it is, before it's queued (the server refuses it without)
  const unsaid = [...form.querySelectorAll<HTMLInputElement>(".fa-other input")].find((i) => !i.value.trim());
  if (unsaid) {
    showToast(`${unsaid.getAttribute("aria-label")?.replace(/: other$/, "") ?? "Other"}: say what it is.`);
    unsaid.focus();
    return;
  }
  const fix = S.captureFix ?? S.fix;
  const hd = S.captureHeading ?? getHeading();
  const extra: Record<string, unknown> = {};
  for (const [k, v] of fd.entries()) {
    if (!k.startsWith("f_")) continue;
    const el = form.querySelector(`[name="${k}"]`) as HTMLInputElement | null;
    extra[k.slice(2)] = el?.type === "checkbox" ? el.checked : el?.type === "number" ? (v ? Number(v) : null) : v;
  }
  form.querySelectorAll<HTMLInputElement>("input[type=checkbox][name^=f_]").forEach((el) => (extra[el.name.slice(2)] = el.checked));
  const assetId = (fd.get("asset_id") as string) || null;
  const meta: Record<string, unknown> = {
    client_uuid: newUuid(),
    form_kind: kind || (S.ws === "operations" ? "ops_triage" : "construction_progress"),
    workspace: S.ws,
    captured_at: new Date(fix?.at ?? Date.now()).toISOString(),
    lat: fix?.lat ?? null,
    lon: fix?.lon ?? null,
    accuracy_m: fix?.accuracy ?? null,
    altitude_m: fix?.altitude ?? null,
    altitude_accuracy_m: fix?.altitudeAccuracy ?? null,
    heading_deg: hd.heading,
    heading_accuracy_deg: hd.accuracy,
    heading_source: hd.heading === null ? "none" : hd.source,
    pitch_deg: hd.pitch,
    roll_deg: hd.roll,
    device: navigator.userAgent.slice(0, 300),
    asset_id: assetId,
    asset_type: (fd.get("asset_type") as string) || null,
    asset_candidates: candidates().slice(0, 8).map((c) => ({ asset_id: c.asset_id, asset_type: c.asset_type, distance_m: c.distance_m, bearing_deg: c.bearing_deg, score: c.score, rank: c.rank })),
    fault_type: (fd.get("fault_type") as string) || null,
    failure_component: (fd.get("failure_component") as string) || null,
    severity: (fd.get("severity") as string) || null,
    project_id: (fd.get("project_id") as string) || null,
    notes: ((fd.get("notes") as string) || "").trim() || null,
    form: Object.keys(extra).length ? extra : null,
  };
  await enqueue({ client_uuid: meta.client_uuid as string, meta, photos: S.photos.map((p) => p.blob), photo_headings: S.photos.map((p) => p.heading) });
  S.photos.forEach((p) => URL.revokeObjectURL(p.url));
  S.photos = [];
  S.captureFix = null;
  S.captureHeading = null;
  showToast(navigator.onLine ? "Capture saved - uploading…" : "Capture saved on the phone - it will upload when you have signal.");
  setView("mine");
}

// ---------------------------------------------------------------------------
// Jobs, projects, mine
// ---------------------------------------------------------------------------

async function loadJobs(): Promise<void> {
  if (!S.fix) return;
  try {
    S.jobs = (await fieldApi.liveJobs(S.fix.lat, S.fix.lon)).jobs;
  } catch {
    S.jobs = [];
  }
  if (S.view === "jobs") renderView();
}

function viewJobs(): HTMLElement {
  return h("div", {},
    h("p", { class: "fa-muted" }, "Open jobs from the public live job feed within 800 m (address and point only - no asset ID)."),
    !S.fix ? h("p", { class: "fa-muted" }, "Needs your location.") : null,
    h("ul", { class: "fa-list" },
      ...S.jobs.map((j) =>
        h("li", { class: "fa-cand" },
          h("span", { class: "fa-dot", style: "background:#d69e2e" }),
          h("span", { class: "fa-cand-main" }, h("strong", {}, j.request_type), h("small", {}, `${j.address ?? "no address"} · ${j.status} · ${directionText(j.distance_m, j.bearing_deg, S.heading.heading)}`)),
          h("button", { class: "fa-btn", onclick: () => { setView("capture"); } }, "Triage"),
        ))),
  );
}

async function loadProjects(): Promise<void> {
  if (S.cfg?.profile === "public") return;
  try {
    S.projects = await fieldApi.projects();
  } catch {
    S.projects = [];
  }
  if (S.view === "projects" || (S.view === "msqa" && !msqaEditing())) renderView();
}

// MSQA site forms (msqa.ts): the project being monitored is the app's selected project
const msqaHost = {
  projectId: () => S.project,
  projects: () => S.projects,
  selectProject: (id: string) => {
    S.project = id || null;
    renderView();
  },
  fix: () => (S.fix ? { lat: S.fix.lat, lon: S.fix.lon, accuracy: S.fix.accuracy } : null),
  heading: () => S.heading.heading,
  rerender: () => {
    if (S.view === "msqa") renderView();
  },
};

async function openProject(id: string, into: HTMLElement): Promise<void> {
  const changed = S.project !== id;
  S.project = id;
  // the nearby list re-ranks with this project's assets first
  if (changed && S.fix) void queryNearby();
  into.textContent = "Loading site…";
  try {
    const site = await fieldApi.projectSite(id);
    projectData = {
      type: "FeatureCollection",
      features: site.pipes.map((p) => ({ type: "Feature", geometry: { type: "LineString", coordinates: p.path }, properties: { asset_id: p.asset_id } }) as GeoJSON.Feature),
    };
    src("project")?.setData(projectData);
    const first = site.pipes[0]?.path?.[0];
    if (first && map) {
      S.follow = false;
      renderMapControls();
      map.easeTo({ center: first as [number, number], zoom: 16 });
    }
    radarAssetsCache = null;
    const mid = site.pipes.find((p) => p.path?.length)?.path;
    const siteHref = mid ? preferredDirectionsLink(navigator.userAgent, mid[Math.floor(mid.length / 2)][1], mid[Math.floor(mid.length / 2)][0], "driving", site.project.name) : null;
    fill(into,
      h("p", {}, `${site.project.name} - ${site.project.phase_label ?? ""} · ${site.pipes.length} pipes · ${site.captures.length} capture(s)`),
      site.pipes.length
        ? h("div", { class: "fa-row fa-guide-entry" },
            h("button", { class: "fa-btn fa-big", onclick: () => void guideToProject(id) }, "➤ Guide me to the nearest pipe"),
            siteHref ? h("a", { class: "fa-btn", href: siteHref, target: "_blank", rel: "noopener" }, "Drive there (Maps)") : null)
        : null,
      site.pipes.length > 1
        ? h("details", { class: "fa-project-pipes" },
            h("summary", {}, `Guide to a particular pipe (${site.pipes.length})`),
            h("ul", { class: "fa-list" }, ...site.pipes.slice(0, 200).map((p) =>
              h("li", { class: "fa-cand", "data-asset-id": p.asset_id, onclick: () => void guideToAsset(p.asset_id) },
                h("span", { class: "fa-cand-main" }, h("strong", {}, p.asset_id), h("small", {}, "Tap to be guided to this pipe")),
                h("span", { class: "fa-arrow" }, "➤")))))
        : null,
      captureList(site.captures),
      // AR scan mode (2026-10-14): the project's scan sessions; admins can delete one
      scanSessionsEl(id, roleAtLeast(getState().currentUser, "admin")),
      h("button", { class: "fa-btn fa-big", onclick: () => setView("capture") }, "Capture progress / as-built / issue"),
      h("button", { class: "fa-btn fa-big", onclick: () => setView("msqa") }, "☑ MSQA site visit / audit"),
    );
  } catch {
    into.textContent = "Couldn't load the site.";
  }
}

function viewProjects(): HTMLElement {
  if (S.cfg?.profile === "public") return h("p", { class: "fa-muted" }, "Projects aren't available for your organisation's data profile.");
  const detail = h("div", { class: "fa-project-detail" });
  if (S.project) void openProject(S.project, detail);
  return h("div", {},
    h("p", { class: "fa-muted" }, "Projects you manage or have tasks on come first, then the rest (construct phase first)."),
    h("ul", { class: "fa-list" },
      ...S.projects.map((p) =>
        h("li", { class: `fa-cand${S.project === p.id ? " sel" : ""}`, onclick: () => void openProject(p.id, detail) },
          h("span", { class: "fa-dot", style: `background:${p.in_construction ? "#805ad5" : "#a0aec0"}` }),
          h("span", { class: "fa-cand-main" }, h("strong", {}, p.name), h("small", {}, `${p.role ? `Yours (${p.role}) · ` : ""}${p.phase_label ?? p.status} · ${p.n_assets} assets · ${p.n_captures} captures`))))),
    detail,
  );
}

function captureList(caps: Capture[]): HTMLElement {
  return h("ul", { class: "fa-list" },
    ...caps.map((c) =>
      h("li", { class: "fa-cap" },
        c.photos[0]?.processed ? h("img", { src: photoUrl(c, c.photos[0], "thumb"), alt: "", loading: "lazy" }) : h("span", { class: "fa-noimg" }, c.photos.length ? "⏳" : "📝"),
        h("span", { class: "fa-cand-main" },
          h("strong", {}, `${c.fault_type ?? c.form_kind} ${c.asset_id ? `· ${c.asset_id}` : ""}`),
          h("small", {}, `${fmtDate(c.received_at)} · ${c.status}${c.road_context?.road ? ` · ${c.road_context.road.name}` : ""}${c.user_name ? ` · ${c.user_name}` : ""}`)),
      )),
  );
}

function viewMine(): HTMLElement {
  const outboxEl = h("div", {});
  const mineEl = h("div", {}, h("p", { class: "fa-muted" }, "Loading your captures…"));
  void (async () => {
    const all = await listOutbox();
    // AR scan-mode frames (2026-10-14) are many small items: one summary line, not a row each
    const scanItems = all.filter((i) => i.kind === "scan_frame" || i.kind === "scan_session" || i.kind === "scan_stop");
    const items = all.filter((i) => !scanItems.includes(i));
    const scanFailed = scanItems.filter((i) => i.failed);
    const scanRow = scanItems.length
      ? h("li", { class: "fa-cand" },
          h("span", { class: "fa-cand-main" }, h("strong", {}, `Scan frames: ${scanItems.filter((i) => i.kind === "scan_frame").length} waiting`),
            h("small", {}, scanFailed.length ? `${scanFailed.length} rejected: ${scanFailed[0].last_error ?? ""}` : "Upload in order when there is signal")),
          h("button", { class: "fa-link", onclick: () => void discardScanQueue().then(() => setView("mine")) }, "Discard scan frames"))
      : null;
    fill(outboxEl, 
      items.length || scanRow ? h("h3", {}, "Waiting to upload") : null,
      h("ul", { class: "fa-list" }, scanRow,
        ...items.map((i) =>
          h("li", { class: "fa-cand" },
            h("span", { class: "fa-cand-main" }, h("strong", {}, i.meta.label ? String(i.meta.label) : `${i.meta.form_kind} · ${i.photos.length} photo(s)`), h("small", {}, i.owner && i.owner !== outboxOwner() ? `Captured by ${i.owner} - uploads when they sign in` : i.failed ? `Rejected: ${i.last_error}` : i.last_error ?? "Queued")),
            i.failed ? h("button", { class: "fa-btn", onclick: () => void retry(i.client_uuid) }, "Retry") : null,
            i.failed ? h("button", { class: "fa-link", onclick: () => void discard(i.client_uuid) }, "Discard") : null))),
      items.length || scanRow ? h("button", { class: "fa-btn", onclick: () => void drain({ force: true }) }, "Send now") : null,
    );
    try {
      fill(mineEl, h("h3", {}, "My recent captures"), captureList(await fieldApi.captures({ mine: true })));
    } catch {
      fill(mineEl, h("p", { class: "fa-muted" }, "Your uploaded captures appear here when you're online."));
    }
  })();
  const offlineEl = h("div", { class: "fa-offline" },
    h("h3", {}, "Offline (experimental)"),
    h("p", { class: "fa-muted" }, "Cache the assets around you so the app can still suggest nearby assets without signal. When you're back online the server's result replaces it."),
    h("button", { class: "fa-btn", onclick: () => void cacheArea() }, "Cache assets for this area"),
    h("button", { class: "fa-link", onclick: () => void Promise.all([clearAreas(), clearTiles()]).then(() => showToast("Offline cache cleared")) }, "Clear cache"),
  );
  void listAreas().then((a) => offlineEl.append(h("small", {}, `${a.length} area(s) cached, ${a.reduce((n, x) => n + x.assets.length, 0)} assets`)));
  const packsEl = viewPacks();
  const account = h("div", { class: "fa-offline" },
    installAdvice(true)
      ? h("button", { class: "fa-link", onclick: () => { resetInstallAdvice(); setView("near"); render(); } }, "Install on this phone (add to home screen)")
      : null,
    // page feedback (pageFeedback.ts): a phone has no right-click
    h("button", { class: "fa-link", onclick: () => openFeedbackDialog(null, null) }, "Give feedback on this app"),
    h("button", { class: "fa-link", onclick: () => void clearTiles().finally(() => document.getElementById("logout-btn")?.click()) }, "Sign out"),
    h("small", { class: "fa-muted" }, " (on a shared phone, sign out when you finish - queued photos stay until uploaded)"));
  return h("div", {}, outboxEl, mineEl, offlineEl, packsEl, account);
}

// ---------------------------------------------------------------------------
// Offline map packs (F8): LINZ LDS tiles via our proxy + the area's assets
// ---------------------------------------------------------------------------

function viewPacks(): HTMLElement {
  const el = h("div", { class: "fa-offline fa-packs" });
  const listEl = h("div", {});
  const estEl = h("div", {});
  const areaSel = h("select", { class: "fa-input", name: "pack_area" },
    h("option", { value: "me" }, "Around me"),
    h("option", { value: "view" }, "The area shown on the map"),
    S.project && S.cfg?.profile !== "public" ? h("option", { value: "project" }, "The selected project's pipes") : null) as HTMLSelectElement;
  const radiusSel = h("select", { class: "fa-input", name: "pack_radius" },
    ...[300, 500, 1000].map((r) => h("option", { value: String(r), selected: r === 500 }, `${r} m radius`))) as HTMLSelectElement;
  const zoomSel = h("select", { class: "fa-input", name: "pack_zmax" },
    h("option", { value: "17" }, "Street detail (zoom 14–17)"),
    h("option", { value: "18", selected: true }, "Lid detail (zoom 14–18)"),
    h("option", { value: "19" }, "Finest (zoom 14–19, 4× bigger)")) as HTMLSelectElement;
  const layerBox = (key: string, label: string, on: boolean) =>
    h("label", { class: "fa-row-check small" }, h("input", { type: "checkbox", name: "pack_layer", value: key, checked: on }), h("span", {}, label));
  const layers = h("div", {}, layerBox("aerial", "Aerial (the best LINZ photos for the area)", true),
    layerBox("topo50", "Topo50 map", false));
  areaSel.addEventListener("change", () => (radiusSel.hidden = areaSel.value !== "me"));

  const chosenArea = (): PackArea | null => {
    if (areaSel.value === "project" && S.project) return { kind: "project", project_id: S.project };
    if (areaSel.value === "view" && map) {
      const b = map.getBounds();
      return { kind: "box", west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() };
    }
    if (!S.fix) return null;
    return { kind: "point", lat: S.fix.lat, lon: S.fix.lon, radius_m: Number(radiusSel.value) };
  };

  const estimate = async () => {
    const area = chosenArea();
    if (!area) {
      showToast("Needs your location (or pick the map area).");
      return;
    }
    const ls = Array.from(layers.querySelectorAll<HTMLInputElement>("input:checked")).map((i) => i.value);
    if (!ls.length) {
      showToast("Pick at least one map layer.");
      return;
    }
    estEl.textContent = "Estimating…";
    try {
      const m = await fieldApi.packManifest(area, 14, Number(zoomSel.value), ls);
      const room = await storageRoom();
      S.packEstimate = { area, manifest: m, free: room?.free ?? null };
    } catch (e) {
      S.packEstimate = null;
      estEl.textContent = `Couldn't estimate: ${(e as Error).message}`;
      return;
    }
    renderEstimate();
  };

  const renderEstimate = () => {
    const pe = S.packEstimate;
    if (!pe) return fill(estEl);
    const m = pe.manifest;
    const tooBig = pe.free !== null && m.est_bytes * 1.2 > pe.free;
    fill(estEl,
      h("p", {}, h("strong", {}, `${m.tiles.toLocaleString()} tiles, about ${m.est_mb} MB`),
        ` · ${m.area_km2.toFixed(2)} km² · zoom ${m.zmin}–${m.zmax} · expires after ${m.expiry_days} days`),
      pe.free !== null ? h("p", { class: "fa-muted" }, `Free space on this phone for WRAP: about ${fmtBytes(pe.free)}.`) : null,
      ...m.reasons.map((r) => h("p", { class: "fa-warn" }, r)),
      m.max_zoom_that_fits !== null ? h("p", { class: "fa-muted" }, `Fits with zoom up to ${m.max_zoom_that_fits}.`) : null,
      tooBig ? h("p", { class: "fa-warn" }, "Not enough free storage for this pack.") : null,
      h("p", { class: "fa-muted" }, m.attribution),
      m.ok && !tooBig && !S.packJob
        ? h("button", { class: "fa-btn fa-primary", onclick: () => void startDownload(pe.area, m) }, `Download ${m.est_mb} MB`)
        : null,
    );
  };

  const renderList = async () => {
    const packs = await listPacks();
    const job = S.packJob;
    fill(listEl,
      job ? h("div", { class: "fa-pack-job" },
        h("strong", {}, `Downloading “${job.name}”`),
        h("progress", { max: job.progress.total, value: job.progress.done }),
        h("small", {}, `${job.progress.done}/${job.progress.total} tiles · ${fmtBytes(job.progress.bytes)}${job.progress.failed ? ` · ${job.progress.failed} failed` : ""}${job.progress.waiting ? ` · ${job.progress.waiting}` : ""}`),
        h("button", { class: "fa-link", onclick: () => job.abort.abort() }, "Cancel")) : null,
      packs.length ? h("ul", { class: "fa-list" }, ...packs.map(packRow)) : h("p", { class: "fa-muted" }, "No map packs on this phone yet."),
    );
  };

  const packRow = (p: StoredPack) => {
    const expired = isExpired(p);
    const days = Math.ceil((Date.parse(p.expires_at) - Date.now()) / 86400000);
    return h("li", { class: "fa-cand" },
      h("span", { class: "fa-cand-main" },
        h("strong", {}, p.name),
        h("small", {}, `${p.status === "complete" ? "Ready" : p.status} · ${p.tiles_done}/${p.tiles_total} tiles · ${fmtBytes(p.bytes)} · ${p.assets_n} assets · ${p.manifest.layers.join(", ")} z${p.manifest.zmin}–${p.manifest.zmax}`),
        h("small", { class: expired ? "fa-expired" : "" }, expired ? "EXPIRED - imagery may be out of date: refresh or delete" : `Saved ${fmtDate(p.created_at)} · expires in ${days} day${days === 1 ? "" : "s"}`)),
      h("button", { class: "fa-link", onclick: () => showPack(p) }, "Show"),
      (p.status !== "complete" || expired) && !S.packJob
        ? h("button", { class: "fa-link", onclick: () => void (expired ? refreshPack(p) : startDownload(p.area, p.manifest, p)) }, expired ? "Refresh" : "Resume")
        : null,
      h("button", { class: "fa-link", onclick: () => void deletePack(p.id).then(() => { showToast("Pack deleted"); void renderList(); }) }, "Delete"));
  };

  const showPack = (p: StoredPack) => {
    const [w, s, e, n] = p.manifest.bbox;
    S.follow = false;
    renderMapControls();
    map?.fitBounds([[w, s], [e, n]], { padding: 20, duration: 400 });
  };

  const refreshPack = async (p: StoredPack) => {
    try {
      const m = await fieldApi.packManifest(p.area, p.manifest.zmin, p.manifest.zmax, p.manifest.layers, p.name);
      await deletePack(p.id);
      await startDownload(p.area, m);
    } catch (e) {
      showToast(`Couldn't refresh: ${(e as Error).message}`);
    }
  };

  const startDownload = async (area: PackArea, m: PackManifest, resume?: StoredPack) => {
    if (S.packJob) return;
    const abort = new AbortController();
    S.packJob = { name: m.name, abort, progress: { done: 0, failed: 0, total: m.tiles, bytes: 0, waiting: null } };
    S.packEstimate = null;
    renderEstimate();
    try {
      void navigator.storage?.persist?.();
    } catch {
      /* not supported */
    }
    let last = 0;
    try {
      const p = await downloadPack(m, area, (prog) => {
        if (!S.packJob) return;
        S.packJob.progress = { ...prog };
        if (Date.now() - last > 500) {
          last = Date.now();
          void renderList();
        }
      }, abort.signal, resume);
      showToast(p.status === "complete" ? `Map pack ready: ${p.tiles_done} tiles, ${fmtBytes(p.bytes)}, ${p.assets_n} assets.` : `Pack ${p.status}: ${p.tiles_done}/${p.tiles_total} tiles saved - tap Resume to finish.`);
    } catch (e) {
      showToast(`Pack download failed: ${(e as Error).message}`);
    } finally {
      S.packJob = null;
      void renderList();
    }
  };

  fill(el,
    h("h3", {}, "Offline map packs"),
    h("p", { class: "fa-muted" }, "Download LINZ aerial imagery and the assets for an area before you lose signal. Tiles come through WRAP's server (rate-limited, licensed CC BY 4.0). Street maps are not packed: OpenFreeMap's terms forbid bulk downloads, so they are only cached as you view them."),
    h("label", { class: "fa-field" }, h("span", {}, "Area"), areaSel),
    radiusSel,
    h("label", { class: "fa-field" }, h("span", {}, "Detail"), zoomSel),
    layers,
    h("button", { class: "fa-btn", onclick: () => void estimate(), disabled: !navigator.onLine }, "Estimate size"),
    estEl,
    listEl,
  );
  renderEstimate();
  void renderList();
  return el;
}

/** Offline with a pack covering the user: switch the map to the LINZ pack. */
async function offlineBasemapCheck(): Promise<void> {
  if (navigator.onLine || S.basemap === "linz" || !S.fix) return;
  if ((await packsCovering(S.fix.lat, S.fix.lon)).length) {
    await setBasemap("linz");
    showToast("Offline: showing your LINZ map pack.");
  }
}

async function cacheArea(): Promise<void> {
  if (!S.fix) {
    showToast("Needs your location.");
    return;
  }
  try {
    const pack = await fieldApi.offlinePack(S.fix.lat, S.fix.lon, 250);
    await saveArea({ center: pack.center, radius_m: pack.radius_m, assets: pack.assets as never });
    showToast(`Cached ${pack.n} assets within ${pack.radius_m} m.`);
    renderView();
  } catch {
    showToast("Couldn't cache this area (are you online?).");
  }
}

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

export function isFieldWorkspace(ws: string | undefined | null): ws is FieldWorkspace {
  return ws === "operations" || ws === "construction";
}

let msqaResumeTried = false;

export async function openFieldApp(ws: FieldWorkspace): Promise<void> {
  if (!root) return;
  S.ws = ws;
  if (!VIEWS[ws].some((v) => v.key === S.view)) S.view = VIEWS[ws][0].key;
  S.open = true;
  root.hidden = false;
  document.body.classList.add("field-open");
  if (!S.cfg) {
    try {
      S.cfg = await fieldApi.config();
    } catch {
      /* rendered with defaults */
    }
  }
  // MSQA: the phone reloaded WRAP with a form open (e.g. while the camera app
  // was in front) - reopen that form where the monitor was (row #7)
  if (!msqaResumeTried && VIEWS[ws].some((v) => v.key === "msqa")) {
    msqaResumeTried = true;
    const pid = await resumeMsqa().catch(() => null);
    if (pid) {
      S.project = pid;
      S.view = "msqa";
    }
  }
  render();
  if (ws === "construction" || S.projects.length === 0) void loadProjects();
  maybeQueryNearby(true);
  maybeQueryArea(true);
  if (navigator.onLine && !S.ldsAvailable) {
    fetch("/api/tiles/lds/available", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: { available?: boolean; reason?: string | null }) => {
        S.ldsAvailable = !!j?.available;
        setAerialReason(S.ldsAvailable ? null : j?.reason ? j.reason.replace(/^LINZ_API_KEY is not configured on this server$/, "the server has no LINZ key configured") : "the server has no LINZ key configured");
        renderMapControls();
      })
      .catch((e) => setAerialReason(`couldn't ask the server (${e instanceof Error ? e.message : String(e)})`));
  }
  void pruneTiles();
  void offlineBasemapCheck();
}

export function closeFieldApp(): void {
  if (!root) return;
  stopAr();
  stopRadar();
  stopGuide(false);
  root.classList.remove("fa-guiding");
  arEl = null;
  S.open = false;
  root.hidden = true;
  document.body.classList.remove("field-open");
}

/** Called once from main.ts. `fieldOnly`: the user has no non-field
 * workspace, so there is no main app to go back to. */
export function initFieldApp(): void {
  if (root) return;
  root = h("div", { id: "field-app", class: "field-app", hidden: true });
  document.body.append(root);
  setOutboxOwner(getState().currentUser?.username ?? null);
  startOutbox();
  let mineTimer: number | null = null;
  const refreshMine = () => {
    void updateBadge();
    if (S.view !== "mine" || mineTimer !== null) return;
    mineTimer = window.setTimeout(() => {
      mineTimer = null;
      if (S.view === "mine") renderView();
    }, 400);
  };
  onOutboxChange(refreshMine);
  onUploaded(refreshMine);
  // the sheet follows rotation / the keyboard / the window size
  let relayout: number | null = null;
  const queueLayout = () => {
    if (relayout !== null || !S.open) return;
    relayout = requestAnimationFrame(() => {
      relayout = null;
      layoutSheet();
    });
  };
  window.addEventListener("resize", queueLayout);
  phoneMq?.addEventListener?.("change", queueLayout);
  try {
    performance.setResourceTimingBufferSize(1500); // the area-tile timings (map tiles fill the default 250)
  } catch {
    /* not supported */
  }
  let lastDraw = 0;
  onHeading((r) => {
    S.heading = r;
    const now = Date.now();
    if (now - lastDraw < 250) return;
    lastDraw = now;
    updateStatus();
    updateMe();
    // the radar eases toward the compass on its own animation loop (radar.ts)
    updateGuide();
    maybeQueryNearby();
  });
  window.addEventListener("online", () => {
    updateStatus();
    retryNow();
  });
  // "Guide me" from asset details, search, My work, a project (directions.ts)
  document.addEventListener(FIELD_GUIDE_EVENT, (e) => void guideFromOutside((e as CustomEvent<FieldGuideDetail>).detail ?? {}));
  // AR calibration applied / undone / reset (ar.ts): the 2D map's amber ring + chip
  document.addEventListener(CALIBRATION_EVENT, () => {
    updateMe();
    renderMapControls();
  });
  // back to the app (screen on, tab shown): retry a failed request at once
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && (S.nearbyError || S.areaError)) retryNow();
  });
  window.addEventListener("offline", () => {
    updateStatus();
    void offlineBasemapCheck();
  });
  // sidebar tab: the "Field" tab panel just offers to (re)open the overlay
  const panel = document.getElementById("field-tab-container");
  document.querySelector<HTMLButtonElement>('#sidebar .tab-btn[data-tab="field"]')?.addEventListener("click", () => {
    const ws = document.body.dataset.workspace;
    if (isFieldWorkspace(ws)) void openFieldApp(ws);
  });
  document.addEventListener("workspace-changed", (e) => {
    const ws = (e as CustomEvent).detail?.workspace as string;
    if (!isFieldWorkspace(ws)) closeFieldApp();
    if (panel) {
      fill(panel, 
        h("p", {}, "The field app opens full-screen. On a phone, add WRAP to your home screen for the best experience."),
        isFieldWorkspace(ws) ? h("button", { class: "fa-btn fa-big", onclick: () => void openFieldApp(ws) }, "Open field app") : null,
        roleAtLeast(getState().currentUser, "configure") ? h("p", { class: "caption" }, "Reviewing field captures: Asset data workspace → Field captures.") : null,
      );
    }
  });
}
